import {
  apercuRegle,
  classer,
  ecart,
  MOTIF_PAR_DEFAUT,
  regleQuiPrend,
} from '@imp/shared/classement-mouvements';
import { couvertureReleve } from '@imp/shared/couverture-releve';
import { messageErreur } from '@imp/shared/message-erreur';
import {
  type AjustementEncaissement,
  type EncaissementProjete,
  type EtatEncaissement,
  projeterEncaissements,
  projeterSolde,
  REGLES_PAR_DEFAUT,
  type ReglesProjection,
} from '@imp/shared/projection-tresorerie';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, ChevronRight, Download, Plus, Trash2, Upload } from 'lucide-react';
import { Fragment, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/modules/auth/AuthContext';
import { formatEuro } from '@/modules/catalogue/api';
import { RapprochementPanel } from '@/modules/pilotage/RapprochementPanel';
import { RecapMargesPanel } from '@/modules/pilotage/RecapMargesPanel';
import {
  type LigneFacturationAffichee,
  listSuiviFacturation,
} from '@/modules/pilotage/facturation-api';
import {
  effacerAjustement,
  enregistrerAjustement,
  getReglesProjection,
  listAjustements,
  setReglesProjection,
} from '@/modules/pilotage/projection-api';
import {
  analyserFichierReleve,
  analyserGrillePrevisionnel,
  analyserListingVentes,
  CATEGORIES,
  createMouvement,
  createTresoPoste,
  createVenteExterne,
  deleteMouvement,
  deleteTresoPoste,
  deleteVenteExterne,
  getSoldeInitial,
  createTresoDetail,
  deleteTresoDetail,
  importerMouvements,
  listTresoDetails,
  type TresoDetail,
  updateTresoDetail,
  type ResultatImportMouvements,
  listMouvements,
  type Mouvement,
  setSoldeInitial,
  setPosteManuel,
  updateMouvement,
  exporterGrilleCsv,
  exporterVentesCsv,
  type FactureCrmVente,
  importerPrevisionnel,
  importerVentes,
  lireFeuillesExcel,
  lireFichierGrille,
  listFacturesCrmVentes,
  listTresoPostes,
  listVentesExternes,
  MOIS_LABELS,
  parseMontantCellule,
  type TresoPoste,
  updateVenteExterne,
  upsertTresoMontant,
  type VenteExterne,
  listReglesClassement,
  reglesPourAnnee,
  installerLignesReleve,
  POSTE_ENCAISSEMENTS_CLIENTS,
  POSTE_MOUVEMENTS_INTERNES,
  regleEntreesParDefaut,
  sensDuPoste,
} from '@/modules/pilotage/tresorerie-api';

const NBSP_FMT = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function fmtCell(n: number | null): string {
  return n === null ? '' : NBSP_FMT.format(n);
}

/**
 * Date du jour au format yyyy-mm-dd, lue sur l'horloge LOCALE.
 *
 * `toISOString()` bascule en UTC : entre minuit et 2 h du matin l'été, il rend
 * la veille — et un chantier terminé « aujourd'hui » passerait pour à venir.
 */
function jourLocal(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

const CLE_DEPLIEES = 'imp.tresorerie.familles-depliees';
const CLE_COMPARER = 'imp.tresorerie.comparer-releve';

function estListeDeTextes(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}

/**
 * Un choix d'affichage retenu dans ce navigateur.
 *
 * Simple confort : en navigation privée, ou si le navigateur refuse le
 * stockage, la page s'ouvre avec la valeur par défaut et fonctionne
 * exactement pareil.
 */
function lireChoix<T>(cle: string, defaut: T, valide: (v: unknown) => v is T): T {
  try {
    const brut = window.localStorage.getItem(cle);
    if (brut === null) return defaut;
    const v: unknown = JSON.parse(brut);
    return valide(v) ? v : defaut;
  } catch {
    return defaut;
  }
}

function retenirChoix(cle: string, valeur: unknown): void {
  try {
    window.localStorage.setItem(cle, JSON.stringify(valeur));
  } catch {
    // Stockage refusé : le choix ne survivra pas à la fermeture, rien de plus.
  }
}

type ChampBase = 'base_55' | 'base_10' | 'base_20' | 'base_exo';

/** Cellule éditable d'un montant de pièce de vente (enregistre à la sortie du champ). */
function VenteCell({
  id,
  champ,
  valeur,
  onSave,
}: {
  id: string;
  champ: ChampBase;
  valeur: number;
  onSave: (id: string, champ: ChampBase, montant: number) => void;
}) {
  return (
    <input
      key={`${id}-${champ}-${String(valeur)}`}
      defaultValue={valeur === 0 ? '' : NBSP_FMT.format(valeur)}
      inputMode="decimal"
      className="hover:border-input focus:border-ring w-[80px] rounded border border-transparent bg-transparent px-1 py-0.5 text-right text-[12px] tabular-nums focus:outline-none"
      onBlur={(e) => {
        const n = parseMontantCellule(e.target.value) ?? 0;
        if (n !== valeur) onSave(id, champ, n);
      }}
    />
  );
}

function montantsParMois(p: TresoPoste): (number | null)[] {
  const v: (number | null)[] = Array.from({ length: 12 }, () => null);
  for (const m of p.treso_montants) v[m.mois - 1] = Number(m.montant);
  return v;
}

function somme(vals: (number | null)[]): number {
  return vals.reduce<number>((s, x) => s + (x ?? 0), 0);
}

/** Cellule éditable d'un montant mensuel (enregistre à la sortie du champ). */
/**
 * Le réalisé sous la case, et sa couleur.
 *
 * Le rouge et le vert disent le SENS de l'argent, jamais la famille du poste :
 * dépenser plus que prévu est rouge, encaisser plus que prévu est vert. Une
 * case sans rien de prévu ni rien de sorti ne montre pas « 0 », elle ne montre
 * rien — un zéro inventé se lit comme une information.
 */
/** « sur le seul mois dont le relevé est complet » / « sur les 8 mois dont… » */
function portee(mois: number): string {
  return mois === 1
    ? 'sur le seul mois dont le relevé est complet'
    : `sur les ${String(mois)} mois dont le relevé est complet`;
}

function LigneRealise({
  realise,
  prevu,
  categorie,
  sansSource,
  postesInconnus = 0,
  moisIncomplet = null,
  surMois = null,
}: {
  realise: number;
  prevu: number | null;
  categorie: string;
  /**
   * Rien ne rattache de mouvement bancaire à ce poste — ni règle, ni classement
   * à la main. Son réalisé n'est pas zéro, il est inconnu.
   */
  sansSource: boolean;
  /**
   * Sur une ligne de famille : combien de ses postes ont un réalisé inconnu.
   *
   * Additionner les seuls postes connus donnerait un réel très inférieur au
   * prévu — SALAIRES saisis à la main manquent, URSSAF couverte par une règle
   * est là — et l'écart s'afficherait en vert, comme une économie. On montre
   * ce qu'on sait, sans couleur, et on dit ce qui manque.
   */
  postesInconnus?: number;
  /**
   * Le relevé s'arrête en cours de mois : le dernier jour importé (`AAAA-MM-JJ`).
   * Le réel du mois est alors une somme partielle face à un prévu entier ; en
   * tirer un écart afficherait une économie qui n'existe pas.
   */
  moisIncomplet?: string | null;
  /**
   * Total annuel : sur combien de mois complets portent RÉEL ET PRÉVU. Comparer
   * douze mois de prévu à neuf mois de relevé donnait un écart sans objet.
   */
  surMois?: number | null;
}) {
  // Un « 0 » sur un poste que rien ne vise se lirait « rien n'a été dépensé »,
  // alors que la dépense est seulement restée dans les non-classés. On montre
  // qu'on ne sait pas, et on dit pourquoi.
  if (sansSource) {
    return (
      <div
        className="text-muted-foreground/60 text-[10px] leading-[13px]"
        title="Aucune règle, et aucun mouvement rangé à la main, ne rattache de mouvement bancaire à ce poste : son réalisé est inconnu, pas nul."
      >
        —
      </div>
    );
  }
  if (postesInconnus > 0) {
    return (
      <div
        className="text-muted-foreground text-[10px] tabular-nums leading-[13px]"
        title={`Réalisé connu ${NBSP_FMT.format(realise)} — mais ${String(postesInconnus)} poste${
          postesInconnus > 1 ? 's' : ''
        } de cette famille n’${postesInconnus > 1 ? 'ont' : 'a'} ni règle ni mouvement rangé à la main : ${
          postesInconnus > 1 ? 'leur' : 'sa'
        } part manque, et l’écart avec le prévu ne voudrait rien dire.${
          surMois === null ? '' : ` Total ${portee(surMois)}.`
        }`}
      >
        ≈ {NBSP_FMT.format(realise)}
        {surMois === null ? null : <span className="opacity-70"> · {surMois} mois</span>}
      </div>
    );
  }
  if (moisIncomplet !== null) {
    const jusquau = new Date(`${moisIncomplet}T12:00:00`).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
    });
    return (
      <div
        className="text-muted-foreground text-[10px] tabular-nums leading-[13px]"
        title={`Relevé importé jusqu’au ${jusquau} seulement : ${NBSP_FMT.format(
          realise,
        )} déjà passés, mais le mois n’est pas fini — l’écart avec le prévu ne voudrait rien dire.`}
      >
        ≈ {NBSP_FMT.format(realise)}
      </div>
    );
  }
  const e = ecart(prevu, realise);
  if (e === null) return <div className="h-[13px]" />;
  const plusEstBon = categorie === 'encaissements' || categorie === 'solde';
  const defavorable = plusEstBon ? e < 0 : e > 0;
  // Un mouvement interne n'est ni une perte ni un gain : pas de couleur.
  const ton =
    Math.abs(e) < 0.01 || categorie === 'internes'
      ? 'text-muted-foreground'
      : defavorable
        ? 'text-prio-high'
        : 'text-money-in';
  return (
    <div
      className={`text-[10px] tabular-nums leading-[13px] ${ton}`}
      title={`Réalisé ${NBSP_FMT.format(realise)} — prévu ${
        prevu === null ? 'rien' : NBSP_FMT.format(prevu)
      } — écart ${e > 0 ? '+' : ''}${NBSP_FMT.format(e)}${
        surMois === null
          ? ''
          : ` — ${portee(surMois)}, prévu compris`
      }`}
    >
      {NBSP_FMT.format(realise)}
      {surMois === null ? null : <span className="opacity-70"> · {surMois} mois</span>}
    </div>
  );
}

function MontantCell({
  posteId,
  mois,
  valeur,
  nbDetails,
  comparaison,
  categorie,
  onSave,
  onOuvrirDetail,
}: {
  posteId: string;
  mois: number;
  valeur: number | null;
  /** Nombre de lignes de détail : au-delà de 0, le détail fait foi. */
  nbDetails: number;
  /** La comparaison au relevé. `null` quand elle est repliée. */
  comparaison: { realise: number; sansSource: boolean; moisIncomplet: string | null } | null;
  categorie: string;
  onSave: (posteId: string, mois: number, montant: number) => void;
  onOuvrirDetail: (posteId: string, mois: number) => void;
}) {
  const sousLigne =
    comparaison === null ? null : (
      <LigneRealise
        realise={comparaison.realise}
        prevu={valeur}
        categorie={categorie}
        sansSource={comparaison.sansSource}
        moisIncomplet={comparaison.moisIncomplet}
      />
    );
  // Une case détaillée n'est plus saisissable : son total est la somme de son
  // détail, et laisser modifier les deux les ferait diverger en silence.
  if (nbDetails > 0) {
    return (
      <button
        type="button"
        onClick={() => {
          onOuvrirDetail(posteId, mois);
        }}
        title={`${String(nbDetails)} ligne(s) de détail — cliquez pour les voir`}
        className="hover:bg-background w-[76px] rounded border border-dashed border-sky-400 px-1 py-0.5 text-right text-[12px] tabular-nums"
      >
        {valeur === null ? '' : NBSP_FMT.format(valeur)}
        {sousLigne}
      </button>
    );
  }
  return (
    <div className="w-[76px]">
      <input
        key={`${posteId}-${String(mois)}-${valeur === null ? '' : String(valeur)}`}
        defaultValue={valeur === null ? '' : NBSP_FMT.format(valeur)}
        inputMode="decimal"
        title="Cliquez pour modifier ce montant, ou double-cliquez pour le détailler"
        className="hover:border-input hover:bg-background focus:border-ring focus:bg-background w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-right text-[12px] tabular-nums focus:outline-none"
        onDoubleClick={() => {
          onOuvrirDetail(posteId, mois);
        }}
        onBlur={(e) => {
          const n = parseMontantCellule(e.target.value);
          if (n !== null && n !== valeur) onSave(posteId, mois, n);
        }}
      />
      {sousLigne}
    </div>
  );
}

/** Ce qu'on lit d'un coup d'œil sur un statut de document. */
const STATUT_DOC: Record<string, string> = {
  brouillon: 'brouillon',
  a_envoyer: 'à envoyer',
  envoye: 'envoyé',
  envoyee: 'envoyée',
  consulte: 'consulté',
  signe: 'SIGNÉ',
  refuse: 'refusé',
  expire: 'expiré',
  converti_facture: 'signé et facturé',
  emise: 'émise',
  partiellement_payee: 'partiellement payée',
  payee: 'payée',
  en_retard: 'EN RETARD',
  litige: 'litige',
  annulee_avoir: 'annulée par un avoir',
  annule: 'annulé',
};

/**
 * Le détail d'une ligne « à facturer » : les pièces du chantier, cliquables.
 *
 * Annoncer « il reste 9 299,83 € à facturer » sans dire sur quoi oblige à
 * rouvrir trois écrans pour vérifier. On donne donc ici les devis et les
 * factures, en marquant clairement ceux qui NE COMPTENT PAS dans les totaux —
 * un brouillon, une facture annulée, l'avoir qui l'annule. Sans cette marque,
 * la somme des lignes affichées ne tomberait jamais sur le total, et l'écran
 * passerait pour faux alors qu'il a raison.
 */
function DetailChantierAFacturer({ ligne }: { ligne: LigneFacturationAffichee }) {
  const devis = ligne.documents.filter((d) => d.type === 'devis');
  const factures = ligne.documents.filter((d) => d.type === 'facture');

  const piece = (d: LigneFacturationAffichee['documents'][number]) => (
    <li key={`${d.type}-${d.id}`} className="flex flex-wrap items-baseline gap-x-2 py-0.5">
      <Link
        to={`/${d.type === 'devis' ? 'devis' : 'factures'}/${d.id}`}
        className="text-primary font-medium hover:underline"
      >
        {d.numero}
      </Link>
      {d.kind && d.kind !== 'standard' ? (
        <span className="text-muted-foreground text-[11px]">{d.kind}</span>
      ) : null}
      <span className="text-muted-foreground text-[11px]">{STATUT_DOC[d.statut] ?? d.statut}</span>
      <span className="tabular-nums">{fmtCell(d.montantHt)} € HT</span>
      <span className="text-muted-foreground text-[11px] tabular-nums">
        ({fmtCell(d.montantTtc)} € TTC)
      </span>
      {d.compte ? null : (
        <span
          className="text-muted-foreground text-[11px] italic"
          title="Ce document n’entre pas dans les totaux de la ligne."
        >
          hors total
        </span>
      )}
    </li>
  );

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div>
        <p className="text-muted-foreground mb-1 text-[11px] font-medium uppercase">Chantier</p>
        <Link to={`/chantiers/${ligne.chantierId}`} className="text-primary hover:underline">
          {ligne.libelle}
        </Link>
        <p className="text-muted-foreground text-[11px]">
          {ligne.numero} · {STATUT_DOC[ligne.statut] ?? ligne.statut}
        </p>
        {ligne.contactId ? (
          <Link
            to={`/clients/${ligne.contactId}`}
            className="text-primary text-[11px] hover:underline"
          >
            {ligne.clientFacture === null
              ? `Fiche client — ${ligne.client}`
              : `Travaux chez ${ligne.client}`}
          </Link>
        ) : null}
        {ligne.clientFacture !== null && ligne.contactFactureId !== null ? (
          <Link
            to={`/clients/${ligne.contactFactureId}`}
            className="text-primary block text-[11px] hover:underline"
          >
            Facturé à {ligne.clientFacture}
          </Link>
        ) : null}
      </div>
      <div>
        <p className="text-muted-foreground mb-1 text-[11px] font-medium uppercase">
          Devis ({devis.length})
        </p>
        {devis.length === 0 ? (
          <p className="text-muted-foreground text-[11px]">Aucun devis dans le CRM.</p>
        ) : (
          <ul>{devis.map(piece)}</ul>
        )}
      </div>
      <div>
        <p className="text-muted-foreground mb-1 text-[11px] font-medium uppercase">
          Factures ({factures.length})
        </p>
        {factures.length === 0 ? (
          <p className="text-muted-foreground text-[11px]">Aucune facture dans le CRM.</p>
        ) : (
          <ul>{factures.map(piece)}</ul>
        )}
      </div>
    </div>
  );
}

export function TresoreriePage() {
  const { profil } = useAuth();
  const queryClient = useQueryClient();
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [statut, setStatut] = useState<string | null>(null);
  const [ajout, setAjout] = useState<{ categorie: string; nom: string } | null>(null);
  // Bilan du dernier import : ce qui a été rapproché, et ce qui reste à vérifier.
  const [rapport, setRapport] = useState<ResultatImportMouvements | null>(null);
  const [moisOuvert, setMoisOuvert] = useState<number | null>(null);
  // Case dont on affiche le détail (poste + mois), sous la grille.
  const [detailOuvert, setDetailOuvert] = useState<{ posteId: string; mois: number } | null>(null);
  // Catégories repliées : on n'affiche que leur sous-total (grille plus courte).
  // Repliées par défaut : 55 lignes sur 12 mois à l'ouverture, c'est trop pour
  // voir où ça dérape. On retient celles qu'on a DÉPLIÉES, et non l'inverse :
  // une famille ajoutée plus tard arrive repliée comme les autres.
  const [depliees, setDepliees] = useState<string[]>(() =>
    lireChoix(CLE_DEPLIEES, [], estListeDeTextes),
  );
  const [onglet, setOnglet] = useState<
    'previsionnel' | 'banque' | 'projection' | 'ventes' | 'marges' | 'facturation'
  >('previsionnel');
  const [moisBanque, setMoisBanque] = useState(new Date().getMonth());
  // Comparer la grille au relevé double la hauteur de chaque ligne : on ne
  // l'impose pas, on l'offre.
  // Activée par défaut : une famille repliée doit montrer prévu ET réel d'un coup
  // d'œil. Le choix de l'utilisateur est retenu.
  const [comparerRealise, setComparerRealise] = useState<boolean>(() =>
    lireChoix(CLE_COMPARER, true, (v): v is boolean => typeof v === 'boolean'),
  );
  const [nouveauMvt, setNouveauMvt] = useState<{
    libelle: string;
    sens: 'debit' | 'credit';
    montant: string;
    jour: string;
  } | null>(null);
  const fichierBanqueRef = useRef<HTMLInputElement>(null);

  // Mode du prochain import bancaire : ajouter les nouveautés (défaut) ou tout
  // remplacer (premier chargement uniquement).
  const remplacerToutRef = useRef(false);
  const [ajoutVente, setAjoutVente] = useState<{
    mois: number;
    numero: string;
    type: 'facture' | 'avoir';
  } | null>(null);
  // Ligne « à facturer » dépliée : une seule à la fois, pour que le tableau
  // reste lisible. Null = tout replié.
  const [ligneDepliee, setLigneDepliee] = useState<string | null>(null);
  const fichierGrilleRef = useRef<HTMLInputElement>(null);
  const fichierVentesRef = useRef<HTMLInputElement>(null);

  const postesQ = useQuery({
    queryKey: ['treso-postes', annee],
    queryFn: () => listTresoPostes(annee),
    enabled: Boolean(profil?.isAdmin),
  });

  const detailsQ = useQuery({
    queryKey: ['treso-details', annee],
    queryFn: () => listTresoDetails(annee),
  });

  const facturationQ = useQuery({
    queryKey: ['suivi-facturation'],
    queryFn: listSuiviFacturation,
  });
  const aFacturer = facturationQ.data ?? [];
  // LES DEUX POCHES D'ARGENT EN ATTENTE. Elles ne se mélangent pas : l'une
  // n'est même pas encore réclamée, l'autre est réclamée et pas arrivée.
  //
  // Les chantiers dont le CRM ignore l'engagement (`null`) sont EXCLUS du
  // total plutôt que comptés pour zéro — et leur nombre est affiché, sans quoi
  // le total paraîtrait complet alors qu'il sous-estime.
  const totalAFacturerHt = aFacturer.reduce((t, l) => t + (l.resteAFacturerHt ?? 0), 0);
  const totalAEncaisserTtc = aFacturer.reduce((t, l) => t + l.resteAEncaisserTtc, 0);
  // Le cumul se fait en HT : additionner un HT et un TTC ne voudrait rien dire.
  const totalAttenduHt = aFacturer.reduce(
    (t, l) => t + (l.resteAFacturerHt ?? 0) + l.resteAEncaisserHt,
    0,
  );
  const nbMontantInconnu = aFacturer.filter((l) => l.resteAFacturerHt === null).length;

  const parMotif = (m: string) => aFacturer.filter((l) => l.motif === m).length;
  const nbUrgent = parMotif('intervention_passee');
  const nbAEncaisser = parMotif('attente_reglement');
  const nbAVenir = parMotif('intervention_a_venir');
  // Les chantiers dont le CRM ne porte aucun document : sur ceux-là, il ne sait
  // pas ce qui a déjà été facturé, et le dit au lieu d'inventer.
  const nbHorsCrm = aFacturer.filter((l) => !l.suiviParLeCrm).length;
  const nbNonPlaces = aFacturer.filter((l) => l.motif === 'engage_non_place').length;

  // Détail rangé par case, pour savoir d'un coup d'œil laquelle est détaillée.
  const detailsParCase = useMemo(() => {
    const par = new Map<string, TresoDetail[]>();
    for (const d of detailsQ.data ?? []) {
      const k = `${d.posteId}-${String(d.mois)}`;
      const l = par.get(k);
      if (l) l.push(d);
      else par.set(k, [d]);
    }
    return par;
  }, [detailsQ.data]);
  const nbDetails = (posteId: string, mois: number): number =>
    detailsParCase.get(`${posteId}-${String(mois)}`)?.length ?? 0;
  const ventesQ = useQuery({
    queryKey: ['ventes-externes', annee],
    queryFn: () => listVentesExternes(annee),
    enabled: Boolean(profil?.isAdmin),
  });
  const reglesClassementQ = useQuery({
    queryKey: ['treso-regles'],
    queryFn: listReglesClassement,
    enabled: Boolean(profil?.isAdmin),
  });

  const mouvementsQ = useQuery({
    queryKey: ['treso-mouvements', annee],
    queryFn: () => listMouvements(annee),
    enabled: Boolean(profil?.isAdmin),
  });
  const soldeInitialQ = useQuery({
    queryKey: ['treso-solde-initial', annee],
    queryFn: () => getSoldeInitial(annee),
    enabled: Boolean(profil?.isAdmin),
  });
  const facturesQ = useQuery({
    queryKey: ['ventes-crm', annee],
    queryFn: () => listFacturesCrmVentes(annee),
    enabled: Boolean(profil?.isAdmin),
  });
  const reglesQ = useQuery({
    queryKey: ['treso-regles-projection', annee],
    queryFn: () => getReglesProjection(annee),
    enabled: Boolean(profil?.isAdmin),
  });
  const ajustementsQ = useQuery({
    queryKey: ['treso-ajustements-projection'],
    queryFn: listAjustements,
    enabled: Boolean(profil?.isAdmin),
  });

  const invalider = () => {
    void queryClient.invalidateQueries({ queryKey: ['treso-postes', annee] });
    void queryClient.invalidateQueries({ queryKey: ['ventes-externes', annee] });
    void queryClient.invalidateQueries({ queryKey: ['treso-mouvements', annee] });
    void queryClient.invalidateQueries({ queryKey: ['treso-solde-initial', annee] });
  };

  const saveMut = useMutation({
    mutationFn: ({ posteId, mois, montant }: { posteId: string; mois: number; montant: number }) =>
      upsertTresoMontant(posteId, mois, montant),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur d'enregistrement : ${e.message}`);
    },
  });

  const invaliderDetail = () => {
    void queryClient.invalidateQueries({ queryKey: ['treso-details', annee] });
    void queryClient.invalidateQueries({ queryKey: ['treso-postes', annee] });
  };
  const ajoutDetailMut = useMutation({
    mutationFn: ({
      posteId,
      mois,
      libelle,
      montant,
      ordre,
    }: {
      posteId: string;
      mois: number;
      libelle: string;
      montant: number;
      ordre: number;
    }) => createTresoDetail(posteId, mois, libelle, montant, ordre),
    onSuccess: invaliderDetail,
  });
  const majDetailMut = useMutation({
    mutationFn: ({
      id,
      posteId,
      mois,
      champs,
    }: {
      id: string;
      posteId: string;
      mois: number;
      champs: { libelle?: string; montant?: number };
    }) => updateTresoDetail(id, posteId, mois, champs),
    onSuccess: invaliderDetail,
  });
  const suppDetailMut = useMutation({
    mutationFn: ({ id, posteId, mois }: { id: string; posteId: string; mois: number }) =>
      deleteTresoDetail(id, posteId, mois),
    onSuccess: invaliderDetail,
  });
  const createMut = useMutation({
    mutationFn: ({ categorie, nom, ordre }: { categorie: string; nom: string; ordre: number }) =>
      createTresoPoste(annee, categorie, nom, ordre),
    onSuccess: () => {
      setAjout(null);
      invalider();
    },
    onError: (e: Error) => {
      setStatut(`Erreur d'ajout : ${e.message}`);
    },
  });
  const installMut = useMutation({
    mutationFn: ({
      manque,
      ordreRegle,
    }: {
      manque: { encaissements: boolean; internes: boolean; regle: boolean };
      ordreRegle: number;
    }) => installerLignesReleve(annee, manque, ordreRegle),
    onSuccess: () => {
      invalider();
      void queryClient.invalidateQueries({ queryKey: ['treso-regles'] });
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteTresoPoste(id),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur de suppression : ${e.message}`);
    },
  });
  const venteUpdateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateVenteExterne>[1] }) =>
      updateVenteExterne(id, patch),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur d'enregistrement de la pièce : ${e.message}`);
    },
  });
  const venteCreateMut = useMutation({
    mutationFn: (v: { numero: string; type: 'facture' | 'avoir'; date_emission: string }) =>
      createVenteExterne(v),
    onSuccess: () => {
      setAjoutVente(null);
      invalider();
    },
    onError: (e: Error) => {
      setStatut(`Erreur d'ajout de la pièce : ${e.message}`);
    },
  });
  const venteDeleteMut = useMutation({
    mutationFn: (id: string) => deleteVenteExterne(id),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur de suppression de la pièce : ${e.message}`);
    },
  });
  const majBaseVente = (id: string, champ: ChampBase, montant: number) => {
    const patch: { base_55?: number; base_10?: number; base_20?: number; base_exo?: number } = {};
    patch[champ] = montant;
    venteUpdateMut.mutate({ id, patch });
  };

  const mvtUpdateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof updateMouvement>[1] }) =>
      updateMouvement(id, patch),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur d'enregistrement du mouvement : ${e.message}`);
    },
  });
  const posteManuelMut = useMutation({
    mutationFn: ({ id, poste }: { id: string; poste: string | null }) => setPosteManuel(id, poste),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Impossible de ranger ce mouvement : ${e.message}`);
    },
  });
  const mvtCreateMut = useMutation({
    mutationFn: (m: { date_mouvement: string; libelle: string; debit: number; credit: number }) =>
      createMouvement(m),
    onSuccess: () => {
      setNouveauMvt(null);
      invalider();
    },
    onError: (e: Error) => {
      setStatut(`Erreur d'ajout du mouvement : ${e.message}`);
    },
  });
  const mvtDeleteMut = useMutation({
    mutationFn: (id: string) => deleteMouvement(id),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur de suppression : ${e.message}`);
    },
  });
  const soldeMut = useMutation({
    mutationFn: (montant: number) => setSoldeInitial(annee, montant),
    onSuccess: invalider,
    onError: (e: Error) => {
      setStatut(`Erreur sur le solde de départ : ${e.message}`);
    },
  });

  const invaliderProjection = () => {
    void queryClient.invalidateQueries({ queryKey: ['treso-regles-projection', annee] });
    void queryClient.invalidateQueries({ queryKey: ['treso-ajustements-projection'] });
  };
  const reglesMut = useMutation({
    mutationFn: (r: ReglesProjection) => setReglesProjection(annee, r),
    onSuccess: invaliderProjection,
    onError: (e: Error) => {
      setStatut(`Erreur sur les réglages de projection : ${e.message}`);
    },
  });
  const ajustementMut = useMutation({
    mutationFn: ({
      chantierId,
      ajustement,
    }: {
      chantierId: string;
      ajustement: AjustementEncaissement;
    }) => enregistrerAjustement(chantierId, ajustement),
    onSuccess: invaliderProjection,
    onError: (e: Error) => {
      setStatut(`Erreur d'enregistrement de l'encaissement : ${e.message}`);
    },
  });
  const ajustementResetMut = useMutation({
    mutationFn: (chantierId: string) => effacerAjustement(chantierId),
    onSuccess: invaliderProjection,
    onError: (e: Error) => {
      setStatut(`Erreur de remise à zéro : ${e.message}`);
    },
  });

  if (!profil?.isAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold tracking-tight">Trésorerie</h1>
        <p className="text-muted-foreground">
          Cette page est réservée aux administrateurs (elle contient les salaires).
        </p>
      </div>
    );
  }

  const postes = postesQ.data ?? [];
  const parCategorie = new Map<string, TresoPoste[]>();
  for (const p of postes) {
    const liste = parCategorie.get(p.categorie) ?? [];
    liste.push(p);
    parCategorie.set(p.categorie, liste);
  }

  const sousTotaux = new Map<string, number[]>();
  for (const cat of CATEGORIES) {
    const liste = parCategorie.get(cat.id) ?? [];
    const totalMois = Array.from({ length: 12 }, (_, i) =>
      liste.reduce((s, p) => s + (montantsParMois(p)[i] ?? 0), 0),
    );
    sousTotaux.set(cat.id, totalMois);
  }
  const totalFrais = Array.from({ length: 12 }, (_, i) =>
    CATEGORIES.filter((c) => c.type === 'charge').reduce(
      (s, c) => s + (sousTotaux.get(c.id)?.[i] ?? 0),
      0,
    ),
  );

  // ----- Ventes par mois (externes Extrabat + factures CRM, avoirs déduits) ---
  const ventesParMois = Array.from({ length: 12 }, () => ({ nb: 0, ht: 0, tva: 0, ttc: 0 }));
  const externesParMois: VenteExterne[][] = Array.from({ length: 12 }, () => []);
  const crmParMois: FactureCrmVente[][] = Array.from({ length: 12 }, () => []);
  for (const v of ventesQ.data ?? []) {
    const cible = ventesParMois[Number(v.date_emission.slice(5, 7)) - 1];
    if (!cible) continue;
    externesParMois[Number(v.date_emission.slice(5, 7)) - 1]?.push(v);
    const signe = v.type === 'avoir' ? -1 : 1;
    const b55 = Number(v.base_55);
    const b10 = Number(v.base_10);
    const b20 = Number(v.base_20);
    const bexo = Number(v.base_exo);
    const ht = b55 + b10 + b20 + bexo;
    const tva = b55 * 0.055 + b10 * 0.1 + b20 * 0.2;
    cible.nb += signe;
    cible.ht += signe * ht;
    cible.tva += signe * tva;
    cible.ttc += signe * (ht + tva);
  }
  for (const f of facturesQ.data ?? []) {
    if (!f.date_emission) continue;
    const cible = ventesParMois[Number(f.date_emission.slice(5, 7)) - 1];
    if (!cible) continue;
    crmParMois[Number(f.date_emission.slice(5, 7)) - 1]?.push(f);
    const signe = f.kind === 'avoir' ? -1 : 1;
    cible.nb += signe;
    cible.ht += signe * Number(f.montant_ht);
    cible.tva += signe * Number(f.montant_tva);
    cible.ttc += signe * Number(f.montant_ttc);
  }
  const ventesTotal = ventesParMois.reduce(
    (s, m) => ({ nb: s.nb + m.nb, ht: s.ht + m.ht, tva: s.tva + m.tva, ttc: s.ttc + m.ttc }),
    { nb: 0, ht: 0, tva: 0, ttc: 0 },
  );

  // Repères de lecture : mois en cours (si on regarde l'année courante) et
  // écart ventes/charges, mois par mois puis sur l'année.
  const aujourdhui = new Date();
  const moisCourant = aujourdhui.getFullYear() === annee ? aujourdhui.getMonth() : -1;
  const resultatParMois = ventesParMois.map((v, i) => v.ttc - (totalFrais[i] ?? 0));
  const chargesAnnee = somme(totalFrais);
  const resultatAnnee = ventesTotal.ttc - chargesAnnee;
  const estReplie = (id: string) => !depliees.includes(id);
  const basculerCategorie = (id: string) => {
    setDepliees((d) => {
      const suite = d.includes(id) ? d.filter((x) => x !== id) : [...d, id];
      retenirChoix(CLE_DEPLIEES, suite);
      return suite;
    });
  };
  // Colonne « Poste » figée à gauche : le libellé reste lisible quand on fait
  // défiler les 12 mois horizontalement.
  const figee = 'sticky left-0 z-10';

  // ----- Suivi bancaire : le solde est recalculé, jamais stocké --------------
  const mouvements = mouvementsQ.data ?? [];

  // ----- Rapprochement : ce que le relevé a réellement payé, poste par poste -
  // Les règles vivent hors de l'année (elles visent le NOM du poste) ; celles
  // dont le poste n'existe pas dans la grille affichée sont mises de côté et
  // signalées, jamais appliquées en silence.
  const { utilisables: reglesUtilisables, orphelines: reglesOrphelines } = reglesPourAnnee(
    reglesClassementQ.data ?? [],
    postes,
  );
  const mouvementsAClasser = (mouvementsQ.data ?? []).map((m) => ({
    id: m.id,
    jour: m.date_mouvement,
    libelle: m.libelle,
    debit: Number(m.debit),
    credit: Number(m.credit),
    posteImpose: m.poste_manuel,
  }));
  // Les postes sont désignés par leur nom partout où l'utilisateur en choisit un :
  // ils sont recréés chaque année, un identifiant mourrait au 1er janvier.
  const posteParNom = new Map(postes.map((p) => [p.poste, p.id]));
  // Un mouvement rangé à la main compte dans le sens de son poste : un chèque
  // impayé rangé dans les encaissements les diminue.
  const sensParPoste = new Map(postes.map((p) => [p.id, sensDuPoste(p.categorie)]));
  const classement = classer(mouvementsAClasser, reglesUtilisables, posteParNom, sensParPoste);
  // ----- Les lignes que le relevé réclame --------------------------------------
  // « Encaissements clients » (avec sa règle « toute entrée non reconnue ») et
  // « Mouvements internes ». Proposées, jamais créées en silence : on montre
  // d'abord ce que la règle rangera.
  const regleDefaut = regleEntreesParDefaut(reglesClassementQ.data ?? []);
  const manqueLignes = {
    encaissements: !postes.some((p) => p.poste === POSTE_ENCAISSEMENTS_CLIENTS),
    internes: !postes.some((p) => p.poste === POSTE_MOUVEMENTS_INTERNES),
    regle: regleDefaut === null,
  };
  const apercuDefaut = manqueLignes.regle
    ? apercuRegle(
        {
          id: 'regle-par-defaut-a-creer',
          posteId: POSTE_ENCAISSEMENTS_CLIENTS,
          motifs: [MOTIF_PAR_DEFAUT],
          sens: 'credit',
          ordre: 0,
          montantMin: null,
          montantMax: null,
        },
        mouvementsAClasser,
        reglesUtilisables,
      )
    : null;
  const ordreRegleDefaut =
    (reglesClassementQ.data ?? []).reduce((max, r) => Math.max(max, r.ordre), 0) + 10;
  // Entrées et sorties restent séparées : les additionner donnait un nombre
  // qui ne veut rien dire — un million sept cent mille euros qui ne sont ni
  // dépensés, ni encaissés.
  const nonClassesSorties = classement.nonClasses.reduce((t, m) => t + Math.max(0, m.debit), 0);
  const nonClassesEntrees = classement.nonClasses.reduce((t, m) => t + Math.max(0, m.credit), 0);
  /**
   * Les postes dont le réalisé est CONNU.
   *
   * Une règle les vise, ou un mouvement y a été rangé à la main. Pour les
   * autres, le réalisé n'est pas zéro : il est inconnu, et un « 0 » se lirait
   * « rien n'a été dépensé ». Les deux voies comptent : un poste alimenté
   * seulement par des chèques rangés à la main est tout aussi renseigné qu'un
   * poste couvert par une règle.
   */
  const postesRenseignes = new Set([
    ...reglesUtilisables.map((r) => r.posteId),
    ...classement.parPoste.keys(),
  ]);
  /**
   * Le réalisé d'une famille, mois par mois, et combien de ses postes manquent.
   *
   * Seuls les postes renseignés sont additionnés ; les autres sont COMPTÉS, pour
   * que la ligne de famille sache qu'elle est incomplète et le dise.
   */
  const realiseDeFamille = (
    liste: readonly TresoPoste[],
  ): { douze: number[]; sansSource: boolean; postesInconnus: number } => {
    const connus = liste.filter((p) => postesRenseignes.has(p.id));
    const douze = Array.from({ length: 12 }, (_, i) =>
      connus.reduce((t, p) => t + (classement.parPoste.get(p.id)?.[i] ?? 0), 0),
    );
    return {
      douze: douze.map((v) => Math.round(v * 100) / 100),
      sansSource: connus.length === 0,
      postesInconnus: liste.length - connus.length,
    };
  };
  const posteNomParId = new Map(postes.map((p) => [p.id, p.poste]));
  /**
   * Le poste qu'une RÈGLE trouverait pour ce mouvement, en ignorant le choix
   * manuel — c'est la réponse à « et si je retire mon choix ? ».
   *
   * Lire `posteParMouvement` à la place afficherait « automatique : X » sur une
   * ligne rangée à la main, alors que vider la case ne donnerait pas X.
   */
  const posteAutoDe = (m: Mouvement): string | null => {
    const regle = regleQuiPrend(
      {
        id: m.id,
        jour: m.date_mouvement,
        libelle: m.libelle,
        debit: Number(m.debit),
        credit: Number(m.credit),
        posteImpose: null,
      },
      reglesUtilisables,
    );
    return regle === null ? null : (posteNomParId.get(regle.posteId) ?? null);
  };
  const postesTriesPourChoix = [...postes].sort((a, b) => a.poste.localeCompare(b.poste, 'fr'));
  /** La comparaison d'une case, ou `null` quand elle est repliée. */
  /**
   * Le rapprochement ne vaut que si les règles ET le relevé ont été lus.
   *
   * Sans règles, tout le relevé paraît « sans poste » ; sans relevé, tout réel
   * paraît nul. Le 06/10/2026, c'est exactement ce que l'écran a affiché :
   * « Aucune règle pour le moment » et 888 mouvements sans poste, alors que les
   * 55 règles étaient en base. Dans ce cas on n'affiche aucun réel plutôt qu'un
   * réel faux ; le bandeau du haut dit ce qui n'a pas pu se charger.
   */
  const rapprochementFiable = !reglesClassementQ.isError && !mouvementsQ.isError;
  // Jusqu'où va le relevé importé : un mois sans relevé n'a pas de réel à 0, il
  // n'a pas de réel du tout ; un mois entamé n'a pas d'écart.
  const couverture = couvertureReleve(
    mouvements.map((m) => m.date_mouvement),
    annee,
  );
  const moisIncompletDe = (i: number): string | null =>
    couverture.parMois[i] === 'partiel' ? couverture.dernierJour : null;
  /** Une somme annuelle restreinte aux mois que le relevé couvre en entier. */
  const sommeSurMoisComplets = (douze: readonly number[]): number =>
    Math.round(
      douze.reduce((t, v, i) => (couverture.parMois[i] === 'complet' ? t + v : t), 0) * 100,
    ) / 100;
  const comparaisonDe = (
    posteId: string,
    moisIndex: number,
  ): { realise: number; sansSource: boolean; moisIncomplet: string | null } | null =>
    comparerRealise && rapprochementFiable && couverture.parMois[moisIndex] !== 'aucun'
      ? {
          realise: classement.parPoste.get(posteId)?.[moisIndex] ?? 0,
          sansSource: !postesRenseignes.has(posteId),
          moisIncomplet: moisIncompletDe(moisIndex),
        }
      : null;
  const soldeInitial = soldeInitialQ.data ?? 0;
  const soldeApres = new Map<string, number>();
  const soldeFinDeMois: (number | null)[] = Array.from({ length: 12 }, () => null);
  const mvtParMois: Mouvement[][] = Array.from({ length: 12 }, () => []);
  let cumul = soldeInitial;
  for (const m of mouvements) {
    cumul += Number(m.credit) - Number(m.debit);
    soldeApres.set(m.id, cumul);
    const i = Number(m.date_mouvement.slice(5, 7)) - 1;
    if (i >= 0 && i < 12) {
      mvtParMois[i]?.push(m);
      soldeFinDeMois[i] = cumul;
    }
  }
  // Un mois sans mouvement garde le solde du dernier mois qui en avait.
  let report = soldeInitial;
  for (let i = 0; i < 12; i += 1) {
    const s = soldeFinDeMois[i] ?? null;
    if (s === null) soldeFinDeMois[i] = report;
    else report = s;
  }
  const mvtDuMois = mvtParMois[moisBanque] ?? [];
  const soldeDebutMois = moisBanque === 0 ? soldeInitial : (soldeFinDeMois[moisBanque - 1] ?? 0);
  const soldeFinMois = soldeFinDeMois[moisBanque] ?? soldeDebutMois;
  const premierMoisNegatif = soldeFinDeMois.findIndex((s) => s !== null && s < 0);

  // ----- Projection : où va le compte, à partir du dernier solde constaté ----
  // Le point de départ est le DERNIER mouvement réel, jamais une moyenne ni le
  // solde de fin d'année : on projette à partir d'un chiffre que la banque a
  // effectivement écrit, à une date qu'on peut citer.
  const dernierMvt = mouvements.length > 0 ? mouvements[mouvements.length - 1] : undefined;
  const dateSolde = dernierMvt?.date_mouvement ?? `${String(annee)}-01-01`;
  const soldeConstate = dernierMvt ? (soldeApres.get(dernierMvt.id) ?? soldeInitial) : soldeInitial;
  // Charges DÉJÀ sorties du compte, mois par mois. Elles sont comprises dans le
  // solde constaté : les redemander au mois en cours les compterait deux fois.
  const chargesReellesParMois = Array.from({ length: 12 }, () => 0);
  for (const m of mouvements) {
    const i = Number(m.date_mouvement.slice(5, 7)) - 1;
    const acc = chargesReellesParMois[i];
    if (acc !== undefined) chargesReellesParMois[i] = acc + Number(m.debit);
  }
  const regles = reglesQ.data ?? REGLES_PAR_DEFAUT;
  const ajustementsParChantier = new Map((ajustementsQ.data ?? []).map((a) => [a.chantierId, a]));
  const encaissementsProjetes = projeterEncaissements(
    aFacturer
      // Ce qui reste à ENCAISSER sur ce chantier, en HT : ce qui n'est pas
      // encore facturé, plus ce qui est facturé et pas encore réglé. Surtout
      // pas le montant engagé : l'acompte déjà tombé sur le compte y serait
      // reprojeté une seconde fois.
      .map((l) => ({
        ligne: l,
        aVenirHt: (l.resteAFacturerHt ?? 0) + l.resteAEncaisserHt,
      }))
      // Sans engagement connu, le chantier n'entre pas dans la projection : on
      // ne sait pas ce qu'il rapportera, et l'inscrire à 0 € l'affirmerait.
      .filter((x) => x.ligne.finIntervention !== null && x.aVenirHt > 0)
      .map(({ ligne, aVenirHt }) => ({
        chantierId: ligne.chantierId,
        numero: ligne.numero,
        libelle: ligne.libelle,
        client: ligne.client,
        finIntervention: ligne.finIntervention ?? '',
        montantHt: Math.round(aVenirHt * 100) / 100,
      })),
    regles,
    ajustementsParChantier,
    dateSolde,
    jourLocal(aujourdhui),
  );
  const projection = projeterSolde({
    soldeConstate,
    dateSolde,
    encaissements: encaissementsProjetes,
    chargesParMois: totalFrais,
    chargesReellesParMois,
    encaissementsPrevusParMois:
      sousTotaux.get('encaissements') ?? Array.from({ length: 12 }, () => null),
    annee,
  });
  const soldeProjeteFinal = projection.mois[projection.mois.length - 1]?.soldeFin ?? soldeConstate;
  // La projection ne vaut que ce que vaut son point de départ : un relevé qui
  // date de six semaines la fait partir d'un solde périmé, sans que rien à
  // l'écran ne le signale. On compte les jours et on le dit.
  const joursDepuisReleve = Math.floor(
    (Date.parse(`${jourLocal(aujourdhui)}T00:00:00Z`) - Date.parse(`${dateSolde}T00:00:00Z`)) /
      86_400_000,
  );
  const relevePerime = annee === aujourdhui.getFullYear() && joursDepuisReleve > 21;

  /**
   * Enregistre une correction sur une ligne d'encaissement.
   *
   * L'ajustement est réécrit en entier à partir de ce qui est AFFICHÉ : ne
   * pousser que le champ modifié ferait réapparaître une date effacée, ou
   * perdrait un montant saisi la veille.
   */
  const majAjustement = (e: EncaissementProjete, patch: Partial<AjustementEncaissement>) => {
    ajustementMut.mutate({
      chantierId: e.chantierId,
      ajustement: {
        etat: e.etat,
        datePrevue: e.dateForcee ? e.date : null,
        montantPrevu: e.montantForce ? e.montant : null,
        note: e.note,
        ...patch,
      },
    });
  };

  const ETATS: { id: EtatEncaissement; label: string }[] = [
    { id: 'attendu', label: 'À encaisser' },
    { id: 'encaisse', label: 'Déjà encaissé' },
    { id: 'exclu', label: 'Ne pas compter' },
  ];

  /** Une ligne d'encaissement, corrigeable : date, montant, état. */
  const ligneEncaissement = (e: EncaissementProjete) => (
    <tr
      key={e.chantierId}
      className={`border-muted/60 hover:bg-muted/30 border-b ${
        e.montantInconnu ? 'bg-amber-50/60' : ''
      }`}
    >
      <td className="py-0.5 pr-2">
        <input
          key={`${e.chantierId}-date-${e.date}`}
          type="date"
          defaultValue={e.date}
          title={
            e.dateForcee
              ? 'Date saisie à la main'
              : `Calculée : fin de chantier + ${String(regles.delaiJours)} jours`
          }
          className={`hover:border-input focus:border-ring rounded border bg-transparent px-1 py-0.5 focus:outline-none ${
            e.dateForcee ? 'border-sky-400' : 'border-transparent'
          }`}
          onBlur={(ev) => {
            const d = ev.target.value;
            if (d && d !== e.date) majAjustement(e, { datePrevue: d });
          }}
        />
      </td>
      <td className="px-2">{e.client}</td>
      <td className="px-2">
        <span className="text-muted-foreground">{e.numero}</span> {e.libelle}
      </td>
      <td className="px-2 tabular-nums">
        {new Date(`${e.finIntervention}T12:00:00`).toLocaleDateString('fr-FR')}
        <span
          className={`ml-1 text-[11px] ${e.travailFait ? 'text-emerald-700' : 'text-muted-foreground'}`}
        >
          {e.travailFait ? 'terminé' : 'à venir'}
        </span>
      </td>
      <td className="px-2 text-right">
        <input
          key={`${e.chantierId}-mt-${String(e.montant)}`}
          defaultValue={e.montant === 0 ? '' : NBSP_FMT.format(e.montant)}
          inputMode="decimal"
          placeholder={e.montantInconnu ? 'à saisir' : ''}
          title={
            e.montantForce
              ? 'Montant saisi à la main'
              : e.montantInconnu
                ? 'Le chantier ne porte aucun montant : saisissez ce que vous attendez'
                : `Calculé : ${fmtCell(e.montantHt)} € HT + TVA${
                    regles.partAcompte > 0
                      ? `, moins ${String(Math.round(regles.partAcompte * 100))} % d'acompte`
                      : ''
                  }`
          }
          className={`hover:border-input focus:border-ring w-[100px] rounded border bg-transparent px-1 py-0.5 text-right tabular-nums text-emerald-700 focus:outline-none ${
            e.montantForce ? 'border-sky-400' : 'border-transparent'
          }`}
          onBlur={(ev) => {
            const n = parseMontantCellule(ev.target.value);
            if (n !== null && n !== e.montant) majAjustement(e, { montantPrevu: n });
          }}
        />
      </td>
      <td className="px-2">
        <select
          value={e.etat}
          className="bg-background rounded border px-1 py-0.5 text-[12px]"
          onChange={(ev) => {
            majAjustement(e, { etat: ev.target.value as EtatEncaissement });
          }}
        >
          {ETATS.map((x) => (
            <option key={x.id} value={x.id}>
              {x.label}
            </option>
          ))}
        </select>
      </td>
      <td className="text-center">
        {e.dateForcee || e.montantForce || e.etat !== 'attendu' ? (
          <button
            type="button"
            title="Revenir au calcul automatique"
            className="text-muted-foreground hover:text-foreground text-[11px] underline"
            onClick={() => {
              ajustementResetMut.mutate(e.chantierId);
            }}
          >
            auto
          </button>
        ) : null}
      </td>
    </tr>
  );

  const enTeteEncaissements = (
    <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
      <th className="py-1.5 pr-2 font-medium">Encaissement attendu</th>
      <th className="px-2 py-1.5 font-medium">Client</th>
      <th className="px-2 py-1.5 font-medium">Chantier</th>
      <th className="px-2 py-1.5 font-medium">Fin de chantier</th>
      <th className="px-2 py-1.5 text-right font-medium">Montant</th>
      <th className="px-2 py-1.5 font-medium">État</th>
      <th className="w-10" />
    </tr>
  );

  const importerGrille = async (file: File) => {
    setStatut('Import de la grille en cours…');
    try {
      const rows = await lireFichierGrille(file);
      const lignes = analyserGrillePrevisionnel(rows);
      if (lignes.length === 0) throw new Error('aucun poste reconnu dans ce fichier');
      const n = await importerPrevisionnel(annee, lignes);
      setStatut(`✅ Grille importée : ${String(n)} postes.`);
      invalider();
    } catch (e) {
      setStatut(`Erreur d'import : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const importerReleve = async (file: File, remplacerTout: boolean) => {
    setStatut('Import du relevé bancaire en cours…');
    setRapport(null);
    try {
      const analyse = await analyserFichierReleve(file, annee);
      if (analyse.mouvements.length === 0)
        throw new Error('aucun mouvement reconnu dans ce fichier');
      const r = await importerMouvements(
        annee,
        analyse.mouvements,
        analyse.soldeInitial,
        remplacerTout,
        analyse.periode,
      );
      setRapport(r);
      const origine =
        analyse.provenance === 'tableur'
          ? 'tableur de suivi'
          : `relevé bancaire${analyse.compte ? ` — ${analyse.compte}` : ''}`;
      const details = [
        r.dejaPresents > 0 ? `${String(r.dejaPresents)} déjà présent(s), inchangé(s)` : null,
        r.rapproches.length > 0
          ? `${String(r.rapproches.length)} rapproché(s) (même date et même montant, libellé différent)`
          : null,
        r.supprimes > 0 ? `${String(r.supprimes)} ancien(s) mouvement(s) remplacé(s)` : null,
        analyse.datesCorrigees > 0
          ? `${String(analyse.datesCorrigees)} date(s) hors du mois de leur feuille corrigée(s)`
          : null,
        analyse.sansDate > 0
          ? `${String(analyse.sansDate)} ligne(s) sans date rattachée(s) au 1er de leur mois`
          : null,
        analyse.ignorees > 0
          ? `${String(analyse.ignorees)} ligne(s) sans montant ignorée(s)`
          : null,
        analyse.soldeFinalBanque !== null
          ? `solde annoncé par la banque : ${fmtCell(analyse.soldeFinalBanque)} € (à comparer au solde affiché)`
          : null,
        analyse.soldeInitial !== null
          ? `solde de départ déduit : ${fmtCell(analyse.soldeInitial)} €`
          : null,
      ].filter(Boolean);

      // Un export bancaire réunit souvent plusieurs comptes : mélangés, ils
      // fausseraient la trésorerie de plusieurs dizaines de milliers d'euros.
      const misDeCote =
        analyse.autresComptes.length > 0
          ? ` ⚠ Non importé${analyse.autresComptes.length > 1 ? 's' : ''}, ce fichier contient aussi : ${analyse.autresComptes.join(', ')}.`
          : '';
      setStatut(
        `✅ ${origine} : ${String(r.ajoutes)} mouvement(s) ajouté(s)${
          details.length > 0 ? ` — ${details.join(', ')}` : ''
        }.${misDeCote}`,
      );
      invalider();
    } catch (e) {
      setStatut(`Erreur d'import : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const importerListing = async (file: File) => {
    setStatut('Import des ventes en cours…');
    try {
      const feuilles = await lireFeuillesExcel(file);
      const lignes = analyserListingVentes(feuilles);
      if (lignes.length === 0) throw new Error('aucune facture reconnue dans ce fichier');
      const n = await importerVentes(lignes);
      setStatut(`✅ Ventes importées : ${String(n)} pièces (factures et avoirs).`);
      invalider();
    } catch (e) {
      setStatut(`Erreur d'import : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const enTete = (
    <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
      <th className={`${figee} bg-card py-1.5 pr-2 font-medium`}>Poste</th>
      {MOIS_LABELS.map((m, i) => (
        <th
          key={m}
          className={`px-1 py-1.5 text-right font-medium ${
            i === moisCourant ? 'bg-primary/10 text-primary rounded-t' : ''
          }`}
          title={i === moisCourant ? 'Mois en cours' : undefined}
        >
          {m}
        </th>
      ))}
      <th className="px-1 py-1.5 text-right font-medium">Annuel</th>
      <th className="w-7" />
    </tr>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm">
              <Link to="/pilotage">
                <ArrowLeft className="h-4 w-4" /> Pilotage
              </Link>
            </Button>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Trésorerie {annee}</h1>
          <p className="text-muted-foreground">
            Grille prévisionnelle et ventes par mois — les sous-totaux sont calculés
            automatiquement. Réservé aux administrateurs.
          </p>
        </div>
        {/* Retour à la ligne : sur un téléphone, les boutons sortaient de l'écran. */}
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={annee}
            onChange={(e) => {
              setAnnee(Number(e.target.value));
            }}
            className="border-input bg-card h-9 rounded-md border px-2 text-sm"
          >
            {[annee - 1, annee, annee + 1]
              .filter((a, i, arr) => arr.indexOf(a) === i)
              .sort()
              .map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
          </select>
          {/* Boutons propres à l'onglet affiché, pour ne pas surcharger la barre */}
          {onglet === 'previsionnel' ? (
            <>
              <Button variant="outline" size="sm" onClick={() => fichierGrilleRef.current?.click()}>
                <Upload className="h-4 w-4" /> Importer la grille
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={postes.length === 0}
                onClick={() => {
                  try {
                    exporterGrilleCsv(annee, postes);
                  } catch (e) {
                    setStatut(`Erreur d'export : ${e instanceof Error ? e.message : String(e)}`);
                  }
                }}
              >
                <Download className="h-4 w-4" /> Exporter le prévisionnel (CSV)
              </Button>
            </>
          ) : null}
          {onglet === 'banque' ? (
            <>
              <Button
                variant="outline"
                size="sm"
                title="Relevé téléchargé depuis votre banque (OFX de préférence, ou CSV) ou tableur de suivi. Ajoute uniquement les mouvements absents : vos pointages et corrections sont conservés."
                onClick={() => {
                  remplacerToutRef.current = false;
                  fichierBanqueRef.current?.click();
                }}
              >
                <Upload className="h-4 w-4" /> Importer le relevé
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Efface les mouvements de l'année et repart du fichier — réservé au tout premier chargement"
                onClick={() => {
                  if (
                    mouvements.length > 0 &&
                    !window.confirm(
                      `Tout remplacer effacera les ${String(mouvements.length)} mouvements de ${String(annee)}, y compris vos pointages et vos saisies manuelles.\n\nContinuer ?`,
                    )
                  )
                    return;
                  remplacerToutRef.current = true;
                  fichierBanqueRef.current?.click();
                }}
              >
                Tout remplacer
              </Button>
            </>
          ) : null}
          {onglet === 'ventes' ? (
            <>
              <Button variant="outline" size="sm" onClick={() => fichierVentesRef.current?.click()}>
                <Upload className="h-4 w-4" /> Importer les ventes
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  try {
                    exporterVentesCsv(
                      annee,
                      ventesParMois,
                      ventesTotal,
                      ventesQ.data ?? [],
                      facturesQ.data ?? [],
                    );
                  } catch (e) {
                    setStatut(`Erreur d'export : ${e instanceof Error ? e.message : String(e)}`);
                  }
                }}
              >
                <Download className="h-4 w-4" /> Exporter les ventes (CSV)
              </Button>
            </>
          ) : null}
          <input
            ref={fichierBanqueRef}
            type="file"
            accept=".ofx,.qfx,.csv,.txt,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importerReleve(f, remplacerToutRef.current);
              e.target.value = '';
            }}
          />
          <input
            ref={fichierGrilleRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importerGrille(f);
              e.target.value = '';
            }}
          />
          <input
            ref={fichierVentesRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importerListing(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      {statut ? <p className="text-muted-foreground text-sm">{statut}</p> : null}

      {/* ---------- Détail d'une case de la grille ---------- */}
      {detailOuvert
        ? (() => {
            const poste = (postesQ.data ?? []).find((p) => p.id === detailOuvert.posteId);
            const lignes =
              detailsParCase.get(`${detailOuvert.posteId}-${String(detailOuvert.mois)}`) ?? [];
            const total = lignes.reduce((t, l) => t + l.montant, 0);
            return (
              <Card className="border-sky-400">
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <CardTitle className="text-base">
                      Détail — {poste?.poste ?? 'poste'} — {MOIS_LABELS[detailOuvert.mois - 1]}{' '}
                      {annee}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setDetailOuvert(null);
                      }}
                    >
                      Fermer
                    </Button>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    Le total de la case est la somme de ces lignes : il se recalcule tout seul.
                    Videz le détail pour ressaisir un montant global.
                  </p>
                </CardHeader>
                <CardContent>
                  <table className="w-full text-sm">
                    <tbody>
                      {lignes.map((l) => (
                        <tr key={l.id} className="border-b last:border-0">
                          <td className="py-1 pr-2">
                            <input
                              defaultValue={l.libelle}
                              className="hover:border-input focus:border-ring w-full rounded border border-transparent bg-transparent px-1 py-0.5 focus:outline-none"
                              onBlur={(e) => {
                                const v = e.target.value.trim();
                                if (v !== '' && v !== l.libelle) {
                                  majDetailMut.mutate({
                                    id: l.id,
                                    posteId: l.posteId,
                                    mois: l.mois,
                                    champs: { libelle: v },
                                  });
                                }
                              }}
                            />
                          </td>
                          <td className="w-32 py-1 pr-2">
                            <input
                              defaultValue={NBSP_FMT.format(l.montant)}
                              inputMode="decimal"
                              className="hover:border-input focus:border-ring w-full rounded border border-transparent bg-transparent px-1 py-0.5 text-right tabular-nums focus:outline-none"
                              onBlur={(e) => {
                                const n = parseMontantCellule(e.target.value);
                                if (n !== null && n !== l.montant) {
                                  majDetailMut.mutate({
                                    id: l.id,
                                    posteId: l.posteId,
                                    mois: l.mois,
                                    champs: { montant: n },
                                  });
                                }
                              }}
                            />
                          </td>
                          <td className="w-8">
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Supprimer cette ligne"
                              onClick={() => {
                                suppDetailMut.mutate({
                                  id: l.id,
                                  posteId: l.posteId,
                                  mois: l.mois,
                                });
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr className="font-medium">
                        <td className="py-1 pr-2">Total</td>
                        <td className="py-1 pr-2 text-right tabular-nums">{fmtCell(total)}</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>

                  <form
                    className="mt-3 flex flex-wrap items-end gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const f = new FormData(e.currentTarget);
                      const lu = (nom: string): string => {
                        const v = f.get(nom);
                        return typeof v === 'string' ? v : '';
                      };
                      const libelle = lu('libelle').trim();
                      const montant = parseMontantCellule(lu('montant'));
                      if (libelle === '' || montant === null) return;
                      ajoutDetailMut.mutate({
                        posteId: detailOuvert.posteId,
                        mois: detailOuvert.mois,
                        libelle,
                        montant,
                        ordre: lignes.length + 1,
                      });
                      e.currentTarget.reset();
                    }}
                  >
                    <input
                      name="libelle"
                      placeholder="Ex. : Solde chantier Dupont, facture FA0000612"
                      className="border-input min-w-[240px] flex-1 rounded border px-2 py-1 text-sm"
                    />
                    <input
                      name="montant"
                      inputMode="decimal"
                      placeholder="Montant"
                      className="border-input w-32 rounded border px-2 py-1 text-right text-sm tabular-nums"
                    />
                    <Button type="submit" size="sm" variant="outline">
                      <Plus className="h-4 w-4" /> Ajouter
                    </Button>
                  </form>
                </CardContent>
              </Card>
            );
          })()
        : null}

      {/* ---------- Rapprochement du dernier import ---------- */}
      {rapport && (rapport.absents.length > 0 || rapport.rapproches.length > 0) ? (
        <Card className={rapport.absents.length > 0 ? 'border-amber-400' : undefined}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base">Rapprochement avec le relevé</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRapport(null);
                }}
              >
                Fermer
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {rapport.absents.length > 0 ? (
              <div>
                <p className="font-medium text-amber-700">
                  ⚠ {rapport.absents.length} ligne(s) inscrite(s) dans le CRM, absente(s) du relevé
                </p>
                <p className="text-muted-foreground mb-2 text-xs">
                  Sur la période couverte par le relevé. À vérifier une par une : opération jamais
                  passée en banque, montant erroné, ou ligne saisie deux fois.
                </p>
                <div className="max-h-56 overflow-y-auto rounded border">
                  <table className="w-full text-xs">
                    <tbody>
                      {rapport.absents.map((a, i) => (
                        <tr key={`${a.date}-${String(i)}`} className="border-b last:border-0">
                          <td className="w-24 py-1 pl-2 tabular-nums">{a.date}</td>
                          <td className="py-1 pr-2">{a.libelle}</td>
                          <td className="w-28 py-1 pr-2 text-right tabular-nums text-red-600">
                            {a.debit > 0 ? fmtCell(a.debit) : ''}
                          </td>
                          <td className="w-28 py-1 pr-2 text-right tabular-nums text-emerald-700">
                            {a.credit > 0 ? fmtCell(a.credit) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {rapport.rapproches.length > 0 ? (
              <details>
                <summary className="cursor-pointer font-medium text-emerald-700">
                  ✓ {rapport.rapproches.length} opération(s) reconnue(s) malgré un libellé différent
                </summary>
                <p className="text-muted-foreground mb-2 mt-1 text-xs">
                  Même date et même montant : la même opération écrite autrement de part et
                  d&apos;autre. Aucun doublon n&apos;a été créé.
                </p>
                <div className="max-h-56 overflow-y-auto rounded border">
                  <table className="w-full text-xs">
                    <tbody>
                      {rapport.rapproches.map((r, i) => (
                        <tr key={`${r.date}-${String(i)}`} className="border-b last:border-0">
                          <td className="w-24 py-1 pl-2 align-top tabular-nums">{r.date}</td>
                          <td className="py-1 pr-2">
                            <div>{r.libelleCrm}</div>
                            <div className="text-muted-foreground">banque : {r.libelleBanque}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* ---------- Synthèse de l'année ---------- */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className={soldeFinMois < 0 ? 'border-red-400' : undefined}>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm">
              {mvtDuMois.length === 0
                ? `Solde au ${new Date(`${dateSolde}T12:00:00`).toLocaleDateString('fr-FR')}`
                : `Solde bancaire fin ${MOIS_LABELS[moisBanque]?.toLowerCase() ?? ''}`}
            </div>
            <div
              className={`mt-1 text-2xl font-bold tabular-nums ${
                soldeFinMois < 0 ? 'text-red-600' : ''
              }`}
            >
              {formatEuro(soldeFinMois)}
            </div>
            <div className="text-muted-foreground mt-1 text-xs">
              {mouvements.length === 0
                ? 'Aucun relevé importé pour le moment'
                : mvtDuMois.length === 0
                  ? `Aucun mouvement en ${MOIS_LABELS[moisBanque]?.toLowerCase() ?? ''} — report du dernier relevé importé`
                  : `${String(mvtDuMois.length)} mouvements en ${
                      MOIS_LABELS[moisBanque]?.toLowerCase() ?? ''
                    } · ${String(mouvements.length)} sur l'année`}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm">Charges de l&apos;année</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">{formatEuro(chargesAnnee)}</div>
            <div className="text-muted-foreground mt-1 text-xs">
              Tout ce qui sort : salaires, fournisseurs, véhicules, frais fixes
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm">Ventes de l&apos;année</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">
              {formatEuro(ventesTotal.ttc)}
            </div>
            <div className="text-muted-foreground mt-1 text-xs">
              {ventesTotal.nb} facture{ventesTotal.nb > 1 ? 's' : ''} TTC, avoirs déduits
            </div>
          </CardContent>
        </Card>
        <Card className={resultatAnnee < 0 ? 'border-red-300' : 'border-emerald-300'}>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-sm">Ventes − charges</div>
            <div
              className={`mt-1 text-2xl font-bold tabular-nums ${
                resultatAnnee < 0 ? 'text-red-600' : 'text-emerald-600'
              }`}
            >
              {formatEuro(resultatAnnee)}
            </div>
            <div className="text-muted-foreground mt-1 text-xs">
              Écart brut de trésorerie — ce n&apos;est pas le résultat comptable
            </div>
          </CardContent>
        </Card>
      </div>

      {premierMoisNegatif >= 0 ? (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          <strong>Attention :</strong> d&apos;après le relevé saisi, le solde bancaire devient
          négatif en {MOIS_LABELS[premierMoisNegatif]} (
          {formatEuro(soldeFinDeMois[premierMoisNegatif] ?? 0)}). Ce relevé ne contient que ce qui
          est déjà passé : pour savoir ce que les chantiers de l&apos;agenda doivent encore
          rapporter, ouvrez l&apos;onglet <strong>Projection</strong>.
        </div>
      ) : null}

      {/* ---------- Code couleur, rappelé une fois pour toutes ---------- */}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="font-medium">Code couleur :</span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm bg-red-100 ring-1 ring-red-300" />
          argent qui sort
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm bg-emerald-100 ring-1 ring-emerald-400" />
          argent qui entre
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm bg-amber-100 ring-1 ring-amber-400" />
          reste à pointer
        </span>
        <span className="flex items-center gap-1.5">
          <span className="bg-primary/10 ring-primary/30 inline-block h-3 w-3 rounded-sm ring-1" />
          mois en cours
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm border border-dashed border-sky-400" />
          case détaillée
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-sm bg-sky-50 ring-1 ring-sky-300" />
          chiffre projeté, pas constaté
        </span>
        <span className="italic">
          les autres teintes ne servent qu&apos;à repérer les familles de postes
        </span>
      </div>

      {/* ---------- Navigation entre les vues ---------- */}
      <div className="bg-muted flex flex-wrap gap-1 rounded-lg p-1">
        {(
          [
            ['previsionnel', 'Prévisionnel', 'Charges prévues par poste et par mois'],
            ['banque', 'Compte bancaire', 'Relevé : entrées, sorties et solde'],
            ['projection', 'Projection', 'Solde à venir, d’après les chantiers placés à l’agenda'],
            ['ventes', 'Ventes', 'Factures et avoirs par mois'],
            ['marges', 'Marges', 'Le récap des marges : une ligne par chantier et par mois'],
            ['facturation', 'À facturer', 'Chantiers placés qui attendent une facture'],
          ] as const
        ).map(([id, label, aide]) => (
          <button
            key={id}
            type="button"
            title={aide}
            onClick={() => {
              setOnglet(id);
            }}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              onglet === id
                ? 'bg-card text-foreground border-border border'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ---------- Grille prévisionnelle ---------- */}
      <Card className={onglet === 'previsionnel' ? undefined : 'hidden'}>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle>Charges par poste</CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              {saveMut.isPending ? (
                <span className="text-muted-foreground text-xs">Enregistrement…</span>
              ) : saveMut.isSuccess ? (
                <span className="text-xs text-emerald-600">✓ Modifications enregistrées</span>
              ) : null}
              {/* Le même export que celui du haut de page, là où l'on regarde la grille. */}
              <Button
                variant="outline"
                size="sm"
                disabled={postes.length === 0}
                title="Un fichier CSV qui s'ouvre dans Excel : un poste par ligne, un mois par colonne, sous-totaux et total compris."
                onClick={() => {
                  try {
                    exporterGrilleCsv(annee, postes);
                  } catch (e) {
                    setStatut(`Erreur d'export : ${e instanceof Error ? e.message : String(e)}`);
                  }
                }}
              >
                <Download className="h-4 w-4" /> Exporter (Excel)
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            Cliquez sur un montant pour le corriger — il s&apos;enregistre en quittant la case. Les
            sous-totaux et le total se recalculent seuls. Cliquez sur le nom d&apos;une famille pour
            la replier.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={comparerRealise}
              onChange={(e) => {
                setComparerRealise(e.target.checked);
                retenirChoix(CLE_COMPARER, e.target.checked);
              }}
            />
            <span>
              <span className="font-medium">Comparer au relevé bancaire.</span>{' '}
              <span className="text-muted-foreground">
                Affiche sous chaque montant ce qui est réellement passé sur le compte
                {classement.nonClasses.length > 0
                  ? ` — ${String(classement.nonClasses.length)} mouvement${
                      classement.nonClasses.length > 1 ? 's' : ''
                    } ne sont encore rattachés à aucun poste.`
                  : '.'}{' '}
                {couverture.dernierJour === null
                  ? 'Aucun relevé importé pour cette année : rien à comparer.'
                  : `Relevé importé jusqu’au ${new Date(
                      `${couverture.dernierJour}T12:00:00`,
                    ).toLocaleDateString('fr-FR')} : au-delà, pas de réel ; le mois entamé est marqué « ≈ ».`}
              </span>
            </span>
          </label>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {postesQ.isLoading ? (
            <p className="text-muted-foreground text-sm">Chargement…</p>
          ) : (
            <table className="w-full min-w-[1080px] border-collapse text-[12px]">
              <thead>{enTete}</thead>
              <tbody>
                {CATEGORIES.filter((c) => c.type === 'charge').map((cat) => {
                  const liste = parCategorie.get(cat.id) ?? [];
                  const st = sousTotaux.get(cat.id) ?? [];
                  const reelFamille = realiseDeFamille(liste);
                  return [
                    <tr key={`${cat.id}-titre`} className={cat.bandeau}>
                      <td
                        colSpan={15}
                        className={`${figee} ${cat.bandeau} ${cat.filet} px-1 py-1.5 text-[11px] font-bold uppercase`}
                      >
                        <span className="flex items-center gap-2">
                          <button
                            type="button"
                            className="hover:text-primary flex items-center gap-1.5"
                            title={
                              estReplie(cat.id) ? 'Déplier cette famille' : 'Replier cette famille'
                            }
                            onClick={() => {
                              basculerCategorie(cat.id);
                            }}
                          >
                            {estReplie(cat.id) ? (
                              <ChevronRight className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5" />
                            )}
                            {cat.label}
                            <span className="text-muted-foreground font-normal normal-case">
                              ({liste.length})
                            </span>
                          </button>
                          <button
                            type="button"
                            title="Ajouter un poste dans cette famille"
                            className="text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setAjout({ categorie: cat.id, nom: '' });
                            }}
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      </td>
                    </tr>,
                    ...(estReplie(cat.id) ? [] : liste).map((p) => {
                      const vals = montantsParMois(p);
                      return (
                        <tr key={p.id} className="border-muted/60 hover:bg-muted/30 border-b">
                          <td
                            className={`${figee} ${cat.filet} bg-card max-w-[220px] truncate py-0.5 pl-1.5 pr-2`}
                            title={p.poste}
                          >
                            {p.poste}
                          </td>
                          {vals.map((v, i) => (
                            <td
                              key={i}
                              className={`px-0.5 text-right ${i === moisCourant ? 'bg-primary/5' : ''}`}
                            >
                              <MontantCell
                                posteId={p.id}
                                mois={i + 1}
                                valeur={v}
                                nbDetails={nbDetails(p.id, i + 1)}
                                comparaison={comparaisonDe(p.id, i)}
                                categorie={cat.id}
                                onSave={(posteId, mois, montant) => {
                                  saveMut.mutate({ posteId, mois, montant });
                                }}
                                onOuvrirDetail={(posteId, mois) => {
                                  setDetailOuvert({ posteId, mois });
                                }}
                              />
                            </td>
                          ))}
                          <td className="px-1 text-right font-medium tabular-nums">
                            {fmtCell(somme(vals))}
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              title="Supprimer ce poste"
                              className="text-muted-foreground hover:text-destructive"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    `Supprimer le poste « ${p.poste} » et ses montants ?`,
                                  )
                                )
                                  deleteMut.mutate(p.id);
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    }),
                    ajout?.categorie === cat.id ? (
                      <tr key={`${cat.id}-ajout`}>
                        <td colSpan={15} className="py-1">
                          <form
                            className="flex items-center gap-2"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const nom = ajout.nom.trim().toUpperCase();
                              if (nom)
                                createMut.mutate({
                                  categorie: cat.id,
                                  nom,
                                  ordre: (liste.length + 1) * 10,
                                });
                            }}
                          >
                            <input
                              value={ajout.nom}
                              onChange={(e) => {
                                setAjout({ categorie: cat.id, nom: e.target.value });
                              }}
                              placeholder="Nom du nouveau poste…"
                              className="bg-background w-64 rounded border px-2 py-1 text-sm"
                            />
                            <Button type="submit" size="sm">
                              Ajouter
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setAjout(null);
                              }}
                            >
                              Annuler
                            </Button>
                          </form>
                        </td>
                      </tr>
                    ) : null,
                    <tr key={`${cat.id}-st`} className="border-b font-semibold">
                      <td className={`${figee} bg-card py-1 pr-2 text-[11px] uppercase`}>
                        Sous-total {cat.label}
                      </td>
                      {st.map((v, i) => (
                        <td
                          key={i}
                          className={`px-1 text-right tabular-nums ${i === moisCourant ? 'bg-primary/5' : ''}`}
                        >
                          {fmtCell(v)}
                          {/* Un mois sans prévu ni réel n'a rien à comparer : dix colonnes de
                              « 0 » sous des zéros encombraient la vue que le repli sert à alléger. */}
                          {comparerRealise &&
                          rapprochementFiable &&
                          liste.length > 0 &&
                          couverture.parMois[i] !== 'aucun' &&
                          (v !== 0 || (reelFamille.douze[i] ?? 0) !== 0) ? (
                            <LigneRealise
                              realise={reelFamille.douze[i] ?? 0}
                              prevu={v}
                              categorie={cat.id}
                              sansSource={reelFamille.sansSource}
                              postesInconnus={reelFamille.postesInconnus}
                              moisIncomplet={moisIncompletDe(i)}
                            />
                          ) : null}
                        </td>
                      ))}
                      <td className="px-1 text-right tabular-nums">
                        {fmtCell(somme(st))}
                        {comparerRealise &&
                        rapprochementFiable &&
                        liste.length > 0 &&
                        couverture.moisComplets > 0 ? (
                          <LigneRealise
                            realise={sommeSurMoisComplets(reelFamille.douze)}
                            prevu={sommeSurMoisComplets(st)}
                            categorie={cat.id}
                            sansSource={reelFamille.sansSource}
                            postesInconnus={reelFamille.postesInconnus}
                            surMois={couverture.moisComplets < 12 ? couverture.moisComplets : null}
                          />
                        ) : null}
                      </td>
                      <td />
                    </tr>,
                  ];
                })}
                <tr className="bg-muted font-bold">
                  <td className={`${figee} bg-muted py-1.5 pr-2 text-[11px] uppercase`}>
                    Total des frais
                  </td>
                  {totalFrais.map((v, i) => (
                    <td
                      key={i}
                      className={`px-1 text-right tabular-nums ${i === moisCourant ? 'bg-primary/10' : ''}`}
                    >
                      {fmtCell(v)}
                    </td>
                  ))}
                  <td className="px-1 text-right tabular-nums">{fmtCell(somme(totalFrais))}</td>
                  <td />
                </tr>
                {/* Rappel des ventes encaissées, puis écart avec les charges */}
                <tr className="text-muted-foreground border-b">
                  <td className={`${figee} bg-card py-1 pr-2 text-[11px] uppercase`}>
                    Ventes facturées (TTC)
                  </td>
                  {ventesParMois.map((v, i) => (
                    <td
                      key={i}
                      className={`px-1 text-right tabular-nums ${i === moisCourant ? 'bg-primary/5' : ''}`}
                    >
                      {v.ttc ? fmtCell(v.ttc) : ''}
                    </td>
                  ))}
                  <td className="px-1 text-right tabular-nums">{fmtCell(ventesTotal.ttc)}</td>
                  <td />
                </tr>
                <tr className="border-b-2 font-bold">
                  <td className={`${figee} bg-card py-1.5 pr-2 text-[11px] uppercase`}>
                    Ventes − charges
                  </td>
                  {resultatParMois.map((v, i) => (
                    <td
                      key={i}
                      className={`px-1 text-right tabular-nums ${
                        i === moisCourant ? 'bg-primary/10' : ''
                      } ${v < 0 ? 'text-red-600' : 'text-emerald-600'}`}
                    >
                      {fmtCell(v)}
                    </td>
                  ))}
                  <td
                    className={`px-1 text-right tabular-nums ${
                      resultatAnnee < 0 ? 'text-red-600' : 'text-emerald-600'
                    }`}
                  >
                    {fmtCell(resultatAnnee)}
                  </td>
                  <td />
                </tr>
                {CATEGORIES.filter((c) => c.type === 'info').map((cat) => {
                  const liste = parCategorie.get(cat.id) ?? [];
                  return liste.map((p) => {
                    const vals = montantsParMois(p);
                    return (
                      <tr key={p.id} className="border-muted/60 border-b">
                        <td
                          className={`${figee} ${cat.filet} bg-card max-w-[220px] truncate py-0.5 pl-1.5 pr-2 text-[11px] font-medium uppercase`}
                          title={p.poste}
                        >
                          {p.poste}
                        </td>
                        {vals.map((v, i) => (
                          <td
                            key={i}
                            className={`px-0.5 text-right ${i === moisCourant ? 'bg-primary/5' : ''}`}
                          >
                            <MontantCell
                              posteId={p.id}
                              mois={i + 1}
                              valeur={v}
                              nbDetails={nbDetails(p.id, i + 1)}
                              comparaison={comparaisonDe(p.id, i)}
                              categorie={cat.id}
                              onSave={(posteId, mois, montant) => {
                                saveMut.mutate({ posteId, mois, montant });
                              }}
                              onOuvrirDetail={(posteId, mois) => {
                                setDetailOuvert({ posteId, mois });
                              }}
                            />
                          </td>
                        ))}
                        <td className="px-1 text-right tabular-nums">
                          {cat.id === 'solde' ? '' : fmtCell(somme(vals))}
                        </td>
                        <td />
                      </tr>
                    );
                  });
                })}
                {/* Les lignes que le relevé réclame, proposées tant qu'elles manquent.
                    Seulement quand règles et relevé sont lus : sinon on ne sait pas
                    ce qui manque, et la règle par défaut existe peut-être déjà. */}
                {(manqueLignes.encaissements || manqueLignes.internes || manqueLignes.regle) &&
                postes.length > 0 &&
                rapprochementFiable &&
                reglesClassementQ.isSuccess &&
                mouvementsQ.isSuccess ? (
                  <tr>
                    <td colSpan={15} className={`${figee} bg-card px-1 py-2`}>
                      <div className="max-w-3xl rounded-md border border-emerald-200 bg-emerald-50 p-3 text-[12px] text-emerald-950">
                        <p className="font-semibold">
                          {manqueLignes.encaissements
                            ? 'Les encaissements clients du relevé n’ont pas de ligne où aller.'
                            : manqueLignes.regle
                              ? 'Les entrées non reconnues du relevé restent sans poste.'
                              : `Pas encore de ligne « ${POSTE_MOUVEMENTS_INTERNES} » cette année.`}
                        </p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-5">
                          {manqueLignes.encaissements ? (
                            <li>
                              Ajouter la ligne <strong>{POSTE_ENCAISSEMENTS_CLIENTS}</strong>, hors
                              total des frais, comme les acomptes.
                            </li>
                          ) : null}
                          {manqueLignes.regle && apercuDefaut !== null ? (
                            <li>
                              Y ranger <strong>toute entrée qu’aucune règle ne reconnaît</strong> :{' '}
                              {apercuDefaut.pris.length === 0 ? (
                                'aucune pour le moment sur ce relevé.'
                              ) : (
                                <>
                                  <strong className="tabular-nums">
                                    {apercuDefaut.pris.length}
                                  </strong>{' '}
                                  entrée{apercuDefaut.pris.length > 1 ? 's' : ''} cette année,{' '}
                                  <strong className="tabular-nums">
                                    {formatEuro(apercuDefaut.entrees)}
                                  </strong>
                                  . Elle passe après toutes les autres règles et ne prend aucune
                                  sortie : un virement venu d’un autre de vos comptes, rangé par
                                  une règle vers {POSTE_MOUVEMENTS_INTERNES}, lui échappe.
                                </>
                              )}
                            </li>
                          ) : null}
                          {manqueLignes.internes ? (
                            <li>
                              Ajouter la ligne <strong>{POSTE_MOUVEMENTS_INTERNES}</strong>, hors
                              charges, pour les virements entre comptes et les chèques impayés.
                            </li>
                          ) : null}
                        </ul>
                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          <Button
                            size="sm"
                            disabled={installMut.isPending}
                            onClick={() => {
                              installMut.mutate({
                                manque: manqueLignes,
                                ordreRegle: ordreRegleDefaut,
                              });
                            }}
                          >
                            {installMut.isPending ? 'Ajout…' : 'Ajouter'}
                          </Button>
                          {installMut.isError ? (
                            <span className="text-destructive">
                              {messageErreur(installMut.error)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ---------- Règles de rapprochement banque / grille ---------- */}
      <Card className={onglet === 'previsionnel' ? undefined : 'hidden'}>
        <CardHeader>
          <CardTitle>Rapprochement avec le compte bancaire</CardTitle>
          <p className="text-muted-foreground text-sm">
            Ce qui relie le relevé à la grille : sans règle, la colonne « réalisé » reste vide.
          </p>
        </CardHeader>
        <CardContent>
          <RapprochementPanel
            indisponible={!rapprochementFiable}
            regles={reglesClassementQ.data ?? []}
            orphelines={reglesOrphelines}
            postes={postes}
            mouvements={mouvementsAClasser}
            nonClasses={classement.nonClasses}
            reglesActives={reglesUtilisables}
            regleParMouvement={classement.regleParMouvement}
            sortiesNonClassees={nonClassesSorties}
            entreesNonClassees={nonClassesEntrees}
          />
        </CardContent>
      </Card>

      {/* ---------- À facturer ---------- */}
      <Card className={onglet === 'facturation' ? undefined : 'hidden'}>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>Chantiers à facturer</CardTitle>
            <span className="text-muted-foreground text-sm">
              {nbUrgent} à facturer · {nbAEncaisser} à encaisser · {nbAVenir} à venir ·{' '}
              {nbNonPlaces} pas encore planifié{nbNonPlaces > 1 ? 's' : ''}
            </span>
          </div>
          <p className="text-muted-foreground text-xs">
            Chantiers engagés — le client a dit oui — sur lesquels il reste de l&apos;argent à faire
            rentrer. Ceux qui sont à l&apos;agenda passent en tête, classés sur leur dernière
            intervention. Un chantier n&apos;en sort que lorsqu&apos;il est{' '}
            <strong>entièrement facturé ET entièrement encaissé</strong> : tant que le virement
            n&apos;est pas tombé, il reste sous vos yeux.
          </p>
          <p className="text-muted-foreground text-xs">
            Les deux dernières colonnes ne disent pas la même chose :{' '}
            <strong>reste à facturer</strong>, c&apos;est ce que le client n&apos;a pas encore reçu
            en facture ; <strong>reste à encaisser</strong>, c&apos;est ce qui est déjà facturé et
            pas encore tombé sur le compte. Cliquez une ligne pour ouvrir ses devis et ses factures.
            Quand une ligne porte la mention <strong>« facturé à »</strong>, les travaux se font
            chez l&apos;un et la facture part chez l&apos;autre — sous-traitance : c&apos;est le
            second nom qui reçoit la facture.
          </p>
          {nbHorsCrm > 0 ? (
            <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <strong>
                {nbHorsCrm} chantier{nbHorsCrm > 1 ? 's' : ''} sur {aFacturer.length} échappe
                {nbHorsCrm > 1 ? 'nt' : ''} encore au CRM
              </strong>{' '}
              : ni devis ni facture d&apos;ici, donc rien ne dit ni ce que vaut le chantier ni ce
              qui a déjà été facturé — leurs montants s&apos;affichent « ? », à confronter à
              Extrabat. Pour les autres, le CRM porte les documents et les chiffres sont exacts.
            </p>
          ) : null}
        </CardHeader>
        <CardContent>
          {facturationQ.isPending ? (
            <p className="text-muted-foreground text-sm">Chargement…</p>
          ) : aFacturer.length === 0 ? (
            <p className="text-sm text-emerald-700">
              ✓ Rien à facturer : aucun chantier engagé n&apos;attend de facture.
            </p>
          ) : (
            <>
              <div className="mb-4 grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border bg-white px-3 py-2 dark:bg-transparent">
                  <p className="text-muted-foreground text-[11px] uppercase">Pas encore facturé</p>
                  <p className="text-lg font-semibold tabular-nums">
                    {fmtCell(Math.round(totalAFacturerHt * 100) / 100)} € HT
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    Travail engagé dont le client n&apos;a pas encore reçu la facture.
                  </p>
                </div>
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                  <p className="text-[11px] uppercase text-amber-900">Facturé, pas encaissé</p>
                  <p className="text-lg font-semibold tabular-nums text-amber-800">
                    {fmtCell(Math.round(totalAEncaisserTtc * 100) / 100)} € TTC
                  </p>
                  <p className="text-[11px] text-amber-900">
                    Factures parties, argent pas arrivé. C&apos;est à relancer.
                  </p>
                </div>
                <div className="rounded-lg border bg-white px-3 py-2 dark:bg-transparent">
                  <p className="text-muted-foreground text-[11px] uppercase">
                    Total à faire rentrer
                  </p>
                  <p className="text-lg font-semibold tabular-nums">
                    {fmtCell(Math.round(totalAttenduHt * 100) / 100)} € HT
                  </p>
                  <p className="text-muted-foreground text-[11px]">
                    Les deux réunis, en HT — additionner un HT et un TTC ne voudrait rien dire.
                  </p>
                </div>
              </div>
              {nbMontantInconnu > 0 ? (
                <p className="text-muted-foreground mb-3 text-[11px]">
                  {nbMontantInconnu} chantier{nbMontantInconnu > 1 ? 's' : ''} sans montant connu
                  {nbMontantInconnu > 1 ? ' ne sont' : " n'est"} pas compté
                  {nbMontantInconnu > 1 ? 's' : ''} dans ces totaux : le vrai montant est donc plus
                  élevé.
                </p>
              ) : null}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] border-collapse text-[12.5px]">
                  <thead>
                    <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
                      <th className="w-6 py-1.5" />
                      <th className="py-1.5 pr-2 font-medium">À faire</th>
                      <th className="px-2 py-1.5 font-medium">Client</th>
                      <th className="px-2 py-1.5 font-medium">Chantier</th>
                      <th className="px-2 py-1.5 font-medium">Intervention</th>
                      <th
                        className="px-2 py-1.5 text-right font-medium"
                        title="Ce que le client a signé : le total HT de ses devis acceptés."
                      >
                        Montant engagé HT
                      </th>
                      <th className="px-2 py-1.5 text-right font-medium">Déjà facturé HT</th>
                      <th
                        className="px-2 py-1.5 text-right font-medium"
                        title="Ce qui n’a pas encore été mis en facture."
                      >
                        Reste à facturer
                      </th>
                      <th
                        className="px-2 py-1.5 text-right font-medium"
                        title="Déjà facturé, pas encore réglé. TTC, comme ce que le client paie."
                      >
                        Reste à encaisser
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {aFacturer.map((l) => {
                      const depliee = ligneDepliee === l.chantierId;
                      return (
                        <Fragment key={l.chantierId}>
                          <tr
                            className={`border-muted/60 hover:bg-muted/30 border-b ${
                              l.interventionPassee ? 'bg-red-50/60' : ''
                            } ${depliee ? 'bg-muted/40' : ''}`}
                          >
                            <td className="py-1 align-top">
                              <button
                                type="button"
                                onClick={() => {
                                  setLigneDepliee(depliee ? null : l.chantierId);
                                }}
                                className="text-muted-foreground hover:text-foreground px-1 leading-none"
                                aria-expanded={depliee}
                                title={
                                  depliee
                                    ? 'Replier'
                                    : 'Voir les devis et factures de ce chantier, et y aller'
                                }
                              >
                                {depliee ? '▾' : '▸'}
                                <span className="sr-only">
                                  {depliee ? 'Replier' : 'Ouvrir'} le détail de {l.libelle}
                                </span>
                              </button>
                            </td>
                            <td className="py-1 pr-2">
                              <span
                                className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                                  l.motif === 'intervention_passee'
                                    ? 'bg-red-100 text-red-700'
                                    : l.motif === 'attente_reglement'
                                      ? 'bg-sky-100 text-sky-800'
                                      : l.motif === 'intervention_a_venir'
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-muted text-muted-foreground'
                                }`}
                                title={
                                  l.motif === 'attente_reglement'
                                    ? 'Tout est facturé : il ne reste plus qu’à être payé.'
                                    : l.motif === 'engage_non_place'
                                      ? 'Le client a signé, mais le chantier n’est pas encore posé à l’agenda.'
                                      : undefined
                                }
                              >
                                {l.motif === 'intervention_passee'
                                  ? 'Travail terminé'
                                  : l.motif === 'attente_reglement'
                                    ? 'À encaisser'
                                    : l.motif === 'intervention_a_venir'
                                      ? 'À venir'
                                      : 'Pas encore planifié'}
                              </span>
                              {l.acompteBrouillon ? (
                                <span
                                  className="text-muted-foreground ml-1 text-[11px]"
                                  title="Un acompte existe en brouillon : il reste à émettre"
                                >
                                  (brouillon en cours)
                                </span>
                              ) : null}
                            </td>
                            <td className="px-2">
                              {l.contactId ? (
                                <Link
                                  to={`/clients/${l.contactId}`}
                                  className="text-primary hover:underline"
                                >
                                  {l.client}
                                </Link>
                              ) : (
                                l.client
                              )}
                              {/* Sous-traitance : travaux chez l'un, facture chez
                                l'autre. La ligne doit dire les deux, sinon on
                                part facturer l'occupant. */}
                              {l.clientFacture !== null ? (
                                <span
                                  className="text-muted-foreground block text-[11px]"
                                  title="Les travaux se font chez le client du chantier, mais le devis signé est adressé à ce client-ci : c’est lui qui recevra la facture."
                                >
                                  facturé à{' '}
                                  {l.contactFactureId !== null ? (
                                    <Link
                                      to={`/clients/${l.contactFactureId}`}
                                      className="text-primary hover:underline"
                                    >
                                      {l.clientFacture}
                                    </Link>
                                  ) : (
                                    l.clientFacture
                                  )}
                                </span>
                              ) : null}
                            </td>
                            <td className="px-2">
                              <Link
                                to={`/chantiers/${l.chantierId}`}
                                className="text-primary hover:underline"
                              >
                                {l.libelle}
                              </Link>
                              <span className="text-muted-foreground ml-1 text-[11px]">
                                {l.numero}
                              </span>
                            </td>
                            <td className="text-muted-foreground px-2 tabular-nums">
                              {l.finIntervention
                                ? new Date(l.finIntervention).toLocaleDateString('fr-FR')
                                : 'pas à l’agenda'}
                              {l.interventionPassee && l.joursDepuis !== null ? (
                                <span className="ml-1 text-[11px] text-red-600">
                                  {l.joursDepuis === 0
                                    ? "aujourd'hui"
                                    : `il y a ${String(l.joursDepuis)} j`}
                                </span>
                              ) : null}
                            </td>
                            <td className="px-2 text-right tabular-nums">
                              {l.montantEngageHt === null ? (
                                <span
                                  className="text-muted-foreground"
                                  title="Ni montant sur la fiche chantier, ni devis signé, ni acompte d’où déduire le total : le CRM ne sait pas ce que vaut ce chantier. Ouvrez la ligne pour voir ses pièces."
                                >
                                  ?
                                </span>
                              ) : (
                                fmtCell(l.montantEngageHt)
                              )}
                            </td>
                            <td className="px-2 text-right tabular-nums">
                              {l.montantFactureHt === 0 ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                fmtCell(l.montantFactureHt)
                              )}
                            </td>
                            <td className="px-2 text-right tabular-nums">
                              {l.resteAFacturerHt === null ? (
                                <span
                                  className="text-muted-foreground"
                                  title="Le CRM ne sait pas ce que vaut ce chantier, donc il ne peut pas dire ce qu’il en reste. Ce qui est déjà facturé, lui, est exact."
                                >
                                  ?
                                </span>
                              ) : (
                                <span className={l.resteAFacturerHt > 0 ? 'font-medium' : ''}>
                                  {fmtCell(l.resteAFacturerHt)}
                                </span>
                              )}
                            </td>
                            <td className="px-2 text-right tabular-nums">
                              {l.resteAEncaisserTtc === 0 ? (
                                <span className="text-muted-foreground" title="Rien en attente.">
                                  —
                                </span>
                              ) : (
                                <span className="font-medium text-amber-700">
                                  {fmtCell(l.resteAEncaisserTtc)}
                                </span>
                              )}
                            </td>
                          </tr>
                          {depliee ? (
                            <tr className="bg-muted/20 border-muted/60 border-b">
                              <td colSpan={9} className="px-3 py-2">
                                <DetailChantierAFacturer ligne={l} />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 text-[12.5px] font-semibold">
                      <td colSpan={5} className="py-1.5 pr-2">
                        Total — {aFacturer.length} chantier{aFacturer.length > 1 ? 's' : ''}
                      </td>
                      {/* Le montant engagé ne se totalise pas : additionner des
                        engagements dont une partie est déjà facturée
                        annoncerait un chiffre d'affaires, pas une attente. */}
                      <td className="text-muted-foreground px-2 text-right text-[11px] font-normal">
                        —
                      </td>
                      <td className="text-muted-foreground px-2 text-right text-[11px] font-normal">
                        —
                      </td>
                      <td className="px-2 text-right tabular-nums">
                        {fmtCell(Math.round(totalAFacturerHt * 100) / 100)}
                      </td>
                      <td className="px-2 text-right tabular-nums text-amber-700">
                        {fmtCell(Math.round(totalAEncaisserTtc * 100) / 100)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ---------- Projection de trésorerie ---------- */}
      <Card className={onglet === 'projection' ? undefined : 'hidden'}>
        <CardHeader>
          <CardTitle>Projection de trésorerie — {annee}</CardTitle>
          <p className="text-muted-foreground text-sm">
            Le compte bancaire dit où vous en êtes. Cette page dit où vous allez : elle repart du
            dernier solde <strong>constaté</strong>, ajoute ce que les chantiers placés à
            l&apos;agenda devraient rapporter, retire les charges qui restent à payer, et déroule le
            solde jusqu&apos;en décembre. Tout ce qui est sur{' '}
            <span className="rounded-sm bg-sky-50 px-1 ring-1 ring-sky-300">fond bleu</span> est
            estimé, jamais constaté — c&apos;est une simulation, pas un relevé.
          </p>
        </CardHeader>
        <CardContent>
          {/* ----- Réglages du calcul ----- */}
          <div className="bg-muted/50 mb-4 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-md px-3 py-3 text-sm">
            <div>
              <label className="text-muted-foreground block text-xs" htmlFor="proj-delai">
                Délai de règlement
              </label>
              <div className="mt-0.5 flex items-center gap-1.5">
                <input
                  id="proj-delai"
                  key={`delai-${String(regles.delaiJours)}`}
                  defaultValue={String(regles.delaiJours)}
                  inputMode="numeric"
                  className="border-input bg-card w-16 rounded border px-2 py-1 text-right tabular-nums"
                  onBlur={(ev) => {
                    const n = Number(ev.target.value.replace(/[^0-9]/g, ''));
                    if (!Number.isNaN(n) && n !== regles.delaiJours)
                      reglesMut.mutate({ ...regles, delaiJours: Math.min(Math.max(n, 0), 365) });
                  }}
                />
                <span className="text-muted-foreground">jours après la fin du chantier</span>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block text-xs" htmlFor="proj-tva">
                TVA appliquée
              </label>
              <div className="mt-0.5 flex items-center gap-1.5">
                <input
                  id="proj-tva"
                  key={`tva-${String(regles.tauxTva)}`}
                  defaultValue={NBSP_FMT.format(regles.tauxTva * 100)}
                  inputMode="decimal"
                  className="border-input bg-card w-16 rounded border px-2 py-1 text-right tabular-nums"
                  onBlur={(ev) => {
                    const n = parseMontantCellule(ev.target.value);
                    if (n !== null && n / 100 !== regles.tauxTva)
                      reglesMut.mutate({
                        ...regles,
                        tauxTva: Math.min(Math.max(n, 0), 100) / 100,
                      });
                  }}
                />
                <span className="text-muted-foreground">% (10 % en rénovation)</span>
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block text-xs" htmlFor="proj-acompte">
                Acompte déjà versé
              </label>
              <div className="mt-0.5 flex items-center gap-1.5">
                <input
                  id="proj-acompte"
                  key={`acompte-${String(regles.partAcompte)}`}
                  defaultValue={NBSP_FMT.format(regles.partAcompte * 100)}
                  inputMode="decimal"
                  className="border-input bg-card w-16 rounded border px-2 py-1 text-right tabular-nums"
                  onBlur={(ev) => {
                    const n = parseMontantCellule(ev.target.value);
                    if (n !== null && n / 100 !== regles.partAcompte)
                      reglesMut.mutate({
                        ...regles,
                        partAcompte: Math.min(Math.max(n, 0), 100) / 100,
                      });
                  }}
                />
                <span className="text-muted-foreground">% déduits du solde à encaisser</span>
              </div>
            </div>
          </div>

          {/* ----- Synthèse ----- */}
          <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-md border px-3 py-2">
              <div className="text-muted-foreground text-xs">
                Solde constaté au {new Date(`${dateSolde}T12:00:00`).toLocaleDateString('fr-FR')}
              </div>
              <div className="mt-0.5 text-xl font-bold tabular-nums">
                {formatEuro(soldeConstate)}
              </div>
              <div className="text-muted-foreground mt-0.5 text-[11px]">
                {dernierMvt
                  ? 'après le dernier mouvement saisi'
                  : 'aucun mouvement : solde de départ'}
              </div>
            </div>
            <div className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2">
              <div className="text-xs text-emerald-900">Encaissements à venir</div>
              <div className="mt-0.5 text-xl font-bold tabular-nums text-emerald-700">
                {formatEuro(projection.totalRetenu)}
              </div>
              <div className="mt-0.5 text-[11px] text-emerald-900/70">
                {projection.retenus.length} chantier(s) de l&apos;agenda
              </div>
            </div>
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2">
              <div className="text-xs text-red-900">Charges restant à payer</div>
              <div className="mt-0.5 text-xl font-bold tabular-nums text-red-700">
                {formatEuro(projection.mois.reduce((t, m) => t + m.decaissements, 0))}
              </div>
              <div className="mt-0.5 text-[11px] text-red-900/70">
                d&apos;après la grille prévisionnelle
              </div>
            </div>
            <div
              className={`rounded-md border px-3 py-2 ${
                soldeProjeteFinal < 0 ? 'border-red-300 bg-red-50' : 'border-sky-300 bg-sky-50'
              }`}
            >
              <div className="text-xs">Solde projeté fin décembre</div>
              <div
                className={`mt-0.5 text-xl font-bold tabular-nums ${
                  soldeProjeteFinal < 0 ? 'text-red-700' : 'text-sky-800'
                }`}
              >
                {formatEuro(soldeProjeteFinal)}
              </div>
              <div className="mt-0.5 text-[11px] opacity-70">
                {projection.creux
                  ? `point bas : ${formatEuro(projection.creux.solde)} en ${
                      MOIS_LABELS[projection.mois[projection.creux.mois]?.mois ?? 0] ?? ''
                    }`
                  : 'aucun mois projeté'}
              </div>
            </div>
          </div>

          {relevePerime ? (
            <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <strong>Relevé à rafraîchir :</strong> la projection part du dernier mouvement connu,
              le {new Date(`${dateSolde}T12:00:00`).toLocaleDateString('fr-FR')}, soit il y a{' '}
              {joursDepuisReleve} jours. Tout ce qui est passé sur le compte depuis manque au
              calcul. Importez votre relevé dans l&apos;onglet <strong>Compte bancaire</strong>
              avant de vous fier au solde ci-dessous.
            </div>
          ) : null}

          {projection.premierMoisNegatif >= 0 ? (
            <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
              <strong>Point de trésorerie à anticiper :</strong> au rythme actuel, le compte
              passerait sous zéro en{' '}
              {MOIS_LABELS[projection.mois[projection.premierMoisNegatif]?.mois ?? 0]} (
              {formatEuro(projection.mois[projection.premierMoisNegatif]?.soldeFin ?? 0)}). Vérifiez
              d&apos;abord les encaissements ci-dessous : un chantier oublié ou une date trop
              tardive suffit à noircir le tableau.
            </div>
          ) : null}

          {/* ----- Solde mois par mois ----- */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-[12.5px]">
              <thead>
                <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
                  <th className="py-1.5 pr-2 font-medium">Mois</th>
                  <th className="px-2 py-1.5 text-right font-medium">Solde début</th>
                  <th className="px-2 py-1.5 text-right font-medium">Encaissements chantiers</th>
                  <th className="px-2 py-1.5 text-right font-medium">Charges à payer</th>
                  <th className="px-2 py-1.5 text-right font-medium">Solde fin</th>
                  <th
                    className="px-2 py-1.5 text-right font-medium"
                    title="Ce que votre grille prévisionnelle annonçait en encaissements. Repère de lecture : ce chiffre n’est PAS ajouté, sinon le même argent compterait deux fois."
                  >
                    Rappel grille
                  </th>
                </tr>
              </thead>
              <tbody>
                {projection.mois.map((m) => (
                  <tr key={m.mois} className="border-muted/60 border-b bg-sky-50/40">
                    <td className="py-1 pr-2 font-medium">
                      {MOIS_LABELS[m.mois]}
                      {m.partiel ? (
                        <span
                          className="text-muted-foreground ml-1 text-[11px]"
                          title={`Mois du dernier relevé : ${fmtCell(m.chargesDejaPayees)} € de charges en sont déjà sorties, elles ne sont pas recomptées.`}
                        >
                          (mois entamé)
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 text-right tabular-nums">{fmtCell(m.soldeDebut)}</td>
                    <td className="px-2 text-right tabular-nums text-emerald-700">
                      {m.encaissements === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span title={`${String(m.nbEncaissements)} chantier(s)`}>
                          + {fmtCell(m.encaissements)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 text-right tabular-nums text-red-700">
                      {m.decaissements === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        `− ${fmtCell(m.decaissements)}`
                      )}
                      {m.partiel && m.chargesDejaPayees > 0 ? (
                        <div
                          className="text-muted-foreground text-[10.5px]"
                          title="Ces charges sont déjà sorties du compte : elles sont comprises dans le solde constaté, on ne les redemande pas."
                        >
                          {fmtCell(m.chargesDejaPayees)} déjà sortis
                        </div>
                      ) : null}
                    </td>
                    <td
                      className={`px-2 text-right font-semibold tabular-nums ${
                        m.soldeFin < 0 ? 'text-red-600' : ''
                      }`}
                    >
                      {fmtCell(m.soldeFin)}
                    </td>
                    <td className="text-muted-foreground px-2 text-right tabular-nums">
                      {m.encaissementsPrevus === null || m.encaissementsPrevus === 0
                        ? '—'
                        : fmtCell(m.encaissementsPrevus)}
                    </td>
                  </tr>
                ))}
                {projection.mois.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-muted-foreground py-3">
                      Rien à projeter : le dernier mouvement bancaire est postérieur à décembre{' '}
                      {annee}.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* ----- Détail des encaissements retenus ----- */}
          <h3 className="mb-1 mt-6 text-sm font-semibold">
            Encaissements comptés dans la projection
          </h3>
          <p className="text-muted-foreground mb-2 text-xs">
            Un chantier par ligne, tel que l&apos;agenda le place. Corrigez la date ou le montant si
            vous savez mieux : votre saisie l&apos;emporte sur le calcul et se conserve.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse text-[12.5px]">
              <thead>{enTeteEncaissements}</thead>
              <tbody>
                {projection.retenus.map((e) => ligneEncaissement(e))}
                {projection.retenus.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-muted-foreground py-3">
                      {facturationQ.isLoading
                        ? 'Lecture des chantiers…'
                        : 'Aucun encaissement à venir : aucun chantier de l’agenda ne tombe entre le dernier relevé et décembre.'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* ----- Attendus dont la date est dépassée ----- */}
          {projection.enRetard.length > 0 ? (
            <>
              <h3 className="mb-1 mt-6 text-sm font-semibold text-amber-800">
                Attendus avant le dernier relevé — {formatEuro(projection.totalEnRetard)}
              </h3>
              <p className="mb-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Ces {projection.enRetard.length} encaissement(s) auraient dû tomber avant le{' '}
                {new Date(`${dateSolde}T12:00:00`).toLocaleDateString('fr-FR')}. Ils ne sont{' '}
                <strong>pas comptés</strong> dans le solde projeté : ils sont vraisemblablement déjà
                sur le compte, et les ajouter gonflerait la trésorerie d&apos;argent déjà reçu. Si
                l&apos;un d&apos;eux n&apos;est pas rentré, donnez-lui sa vraie date : il rejoindra
                la projection.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] border-collapse text-[12.5px]">
                  <thead>{enTeteEncaissements}</thead>
                  <tbody>{projection.enRetard.map((e) => ligneEncaissement(e))}</tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* ----- Au-delà de décembre ----- */}
          {projection.horsHorizon.length > 0 ? (
            <>
              <h3 className="mb-1 mt-6 text-sm font-semibold">
                Après décembre {annee} —{' '}
                {formatEuro(projection.horsHorizon.reduce((t, e) => t + e.montant, 0))}
              </h3>
              <p className="text-muted-foreground mb-2 text-xs">
                Hors du tableau : la grille prévisionnelle s&apos;arrête à décembre, et un solde
                calculé sur les seuls encaissements de l&apos;an prochain serait faussement
                rassurant.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] border-collapse text-[12.5px]">
                  <thead>{enTeteEncaissements}</thead>
                  <tbody>{projection.horsHorizon.map((e) => ligneEncaissement(e))}</tbody>
                </table>
              </div>
            </>
          ) : null}

          {/* ----- Mis de côté à la main ----- */}
          {projection.ecartes.length > 0 ? (
            <>
              <h3 className="text-muted-foreground mb-1 mt-6 text-sm font-semibold">
                Mis de côté — {projection.ecartes.length} chantier(s)
              </h3>
              <p className="text-muted-foreground mb-2 text-xs">
                Déjà encaissés ou volontairement écartés. Ils restent ici, jamais supprimés :
                remettez-les à « à encaisser » et ils reviennent dans le calcul.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[860px] border-collapse text-[12.5px]">
                  <thead>{enTeteEncaissements}</thead>
                  <tbody>{projection.ecartes.map((e) => ligneEncaissement(e))}</tbody>
                </table>
              </div>
            </>
          ) : null}

          <p className="text-muted-foreground mt-6 text-xs">
            Deux choses que cette projection ne sait pas : les acomptes déjà facturés dans Extrabat
            (le CRM ne les voit pas — d&apos;où le réglage ci-dessus), et la TVA collectée, qui
            entre sur le compte mais devra en ressortir. Traitez le solde projeté comme un ordre de
            grandeur, pas comme une promesse.
          </p>
        </CardContent>
      </Card>

      {/* ---------- Compte bancaire ---------- */}
      <Card className={onglet === 'banque' ? undefined : 'hidden'}>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>
              Compte bancaire — {MOIS_LABELS[moisBanque]} {annee}
            </CardTitle>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">Solde au 1er janvier :</span>
              <input
                key={`solde-${String(annee)}-${String(soldeInitial)}`}
                defaultValue={NBSP_FMT.format(soldeInitial)}
                inputMode="decimal"
                className="w-[110px] rounded border px-2 py-1 text-right tabular-nums"
                onBlur={(e) => {
                  const n = parseMontantCellule(e.target.value);
                  if (n !== null && n !== soldeInitial) soldeMut.mutate(n);
                }}
              />
            </div>
          </div>
          <p className="text-muted-foreground text-sm">
            Le solde se recalcule tout seul à chaque ligne : il ne peut pas être faux, même après
            une correction. Cochez une ligne quand vous l&apos;avez retrouvée sur votre relevé.
          </p>
        </CardHeader>
        <CardContent>
          {/* Choix du mois */}
          <div className="mb-4 flex flex-wrap gap-1">
            {MOIS_LABELS.map((m, i) => {
              const solde = soldeFinDeMois[i] ?? null;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMoisBanque(i);
                  }}
                  title={solde === null ? undefined : `Solde fin ${m} : ${formatEuro(solde)}`}
                  className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                    i === moisBanque
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'hover:bg-muted'
                  } ${solde !== null && solde < 0 && i !== moisBanque ? 'border-red-300 text-red-600' : ''}`}
                >
                  {m}
                  <span className="ml-1 opacity-70">({(mvtParMois[i] ?? []).length})</span>
                </button>
              );
            })}
          </div>

          {/* Bandeau début / fin de mois */}
          <div className="bg-muted mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md px-3 py-2 text-sm">
            <span>
              Solde au début du mois :{' '}
              <strong className="tabular-nums">{formatEuro(soldeDebutMois)}</strong>
            </span>
            <span>
              Solde à la fin du mois :{' '}
              <strong className={`tabular-nums ${soldeFinMois < 0 ? 'text-red-600' : ''}`}>
                {formatEuro(soldeFinMois)}
              </strong>
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-[12.5px]">
              <thead>
                <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
                  <th className="py-1.5 pr-2 font-medium">Date</th>
                  <th className="px-2 py-1.5 font-medium">Libellé</th>
                  <th className="px-2 py-1.5 text-right font-medium">Sortie</th>
                  <th className="px-2 py-1.5 text-right font-medium">Entrée</th>
                  <th className="px-2 py-1.5 text-right font-medium">Solde</th>
                  <th
                    className="px-2 py-1.5 font-medium"
                    title="Le poste du prévisionnel que ce mouvement alimente"
                  >
                    Poste
                  </th>
                  <th
                    className="px-2 py-1.5 text-center font-medium"
                    title="Retrouvé sur le relevé bancaire"
                  >
                    Pointé
                  </th>
                  <th className="w-7" />
                </tr>
              </thead>
              <tbody>
                {mvtDuMois.map((m) => {
                  const solde = soldeApres.get(m.id) ?? 0;
                  const debit = Number(m.debit);
                  const credit = Number(m.credit);
                  const posteAuto = posteAutoDe(m);
                  return (
                    <tr
                      key={m.id}
                      className={`border-muted/60 hover:bg-muted/30 border-b ${
                        m.pointe ? '' : 'bg-amber-50/50'
                      }`}
                    >
                      <td className="py-0.5 pr-2">
                        <input
                          key={`${m.id}-d-${m.date_mouvement}`}
                          type="date"
                          defaultValue={m.date_mouvement}
                          className="hover:border-input focus:border-ring rounded border border-transparent bg-transparent px-1 py-0.5 focus:outline-none"
                          onBlur={(e) => {
                            const d = e.target.value;
                            if (d && d !== m.date_mouvement)
                              mvtUpdateMut.mutate({ id: m.id, patch: { date_mouvement: d } });
                          }}
                        />
                      </td>
                      <td className="px-2">
                        <input
                          key={`${m.id}-l-${m.libelle}`}
                          defaultValue={m.libelle}
                          className="hover:border-input focus:border-ring w-full min-w-[180px] rounded border border-transparent bg-transparent px-1 py-0.5 focus:outline-none"
                          onBlur={(e) => {
                            const l = e.target.value.trim();
                            if (l && l !== m.libelle)
                              mvtUpdateMut.mutate({ id: m.id, patch: { libelle: l } });
                          }}
                        />
                      </td>
                      <td className="px-2 text-right">
                        <input
                          key={`${m.id}-deb-${String(debit)}`}
                          defaultValue={debit === 0 ? '' : NBSP_FMT.format(debit)}
                          inputMode="decimal"
                          className="hover:border-input focus:border-ring w-[92px] rounded border border-transparent bg-transparent px-1 py-0.5 text-right tabular-nums text-red-700 focus:outline-none"
                          onBlur={(e) => {
                            const n = parseMontantCellule(e.target.value) ?? 0;
                            if (n !== debit) mvtUpdateMut.mutate({ id: m.id, patch: { debit: n } });
                          }}
                        />
                      </td>
                      <td className="px-2 text-right">
                        <input
                          key={`${m.id}-cre-${String(credit)}`}
                          defaultValue={credit === 0 ? '' : NBSP_FMT.format(credit)}
                          inputMode="decimal"
                          className="hover:border-input focus:border-ring w-[92px] rounded border border-transparent bg-transparent px-1 py-0.5 text-right tabular-nums text-emerald-700 focus:outline-none"
                          onBlur={(e) => {
                            const n = parseMontantCellule(e.target.value) ?? 0;
                            if (n !== credit)
                              mvtUpdateMut.mutate({ id: m.id, patch: { credit: n } });
                          }}
                        />
                      </td>
                      <td
                        className={`px-2 text-right font-medium tabular-nums ${
                          solde < 0 ? 'text-red-600' : ''
                        }`}
                      >
                        {fmtCell(solde)}
                      </td>
                      <td className="px-2">
                        <select
                          value={m.poste_manuel ?? ''}
                          aria-label={`Poste de « ${m.libelle} »`}
                          title={
                            m.poste_manuel === null
                              ? (posteAuto ??
                                'Aucune règle ne reconnaît ce libellé : choisissez un poste pour le ranger à la main.')
                              : `Rangé à la main dans « ${m.poste_manuel} » : aucune règle ne peut le déplacer.`
                          }
                          className={`w-[170px] max-w-[170px] rounded border bg-transparent px-1 py-0.5 text-[11.5px] ${
                            m.poste_manuel !== null
                              ? 'border-sky-300 font-medium text-sky-800'
                              : posteAuto === null
                                ? 'border-amber-300 text-amber-800'
                                : 'text-muted-foreground border-transparent hover:border-input'
                          }`}
                          onChange={(e) => {
                            posteManuelMut.mutate({
                              id: m.id,
                              poste: e.target.value === '' ? null : e.target.value,
                            });
                          }}
                        >
                          <option value="">
                            {posteAuto === null ? 'sans poste' : `auto : ${posteAuto}`}
                          </option>
                          {postesTriesPourChoix.map((p) => (
                            <option key={p.id} value={p.poste}>
                              {p.poste}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 text-center">
                        <input
                          type="checkbox"
                          checked={m.pointe}
                          title={m.pointe ? 'Pointé' : 'Pas encore pointé'}
                          onChange={(e) => {
                            mvtUpdateMut.mutate({ id: m.id, patch: { pointe: e.target.checked } });
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          title="Supprimer ce mouvement"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            if (window.confirm(`Supprimer « ${m.libelle} » ?`))
                              mvtDeleteMut.mutate(m.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {mvtDuMois.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-muted-foreground py-3">
                      Aucun mouvement ce mois-ci. Importez votre relevé Excel ou ajoutez une ligne.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {nouveauMvt ? (
            <form
              className="mt-3 flex flex-wrap items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const libelle = nouveauMvt.libelle.trim();
                const montant = parseMontantCellule(nouveauMvt.montant) ?? 0;
                const jour = Math.min(Math.max(Number(nouveauMvt.jour) || 1, 1), 28);
                if (!libelle || montant === 0) return;
                mvtCreateMut.mutate({
                  date_mouvement: `${String(annee)}-${String(moisBanque + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`,
                  libelle,
                  debit: nouveauMvt.sens === 'debit' ? montant : 0,
                  credit: nouveauMvt.sens === 'credit' ? montant : 0,
                });
              }}
            >
              <input
                value={nouveauMvt.jour}
                onChange={(e) => {
                  setNouveauMvt({ ...nouveauMvt, jour: e.target.value });
                }}
                placeholder="Jour"
                className="bg-background w-16 rounded border px-2 py-1 text-sm"
              />
              <input
                value={nouveauMvt.libelle}
                onChange={(e) => {
                  setNouveauMvt({ ...nouveauMvt, libelle: e.target.value });
                }}
                placeholder="Libellé (ex. FOURNISSEUR X)"
                className="bg-background w-64 rounded border px-2 py-1 text-sm"
              />
              <select
                value={nouveauMvt.sens}
                onChange={(e) => {
                  setNouveauMvt({ ...nouveauMvt, sens: e.target.value as 'debit' | 'credit' });
                }}
                className="bg-background rounded border px-2 py-1 text-sm"
              >
                <option value="debit">Sortie</option>
                <option value="credit">Entrée</option>
              </select>
              <input
                value={nouveauMvt.montant}
                onChange={(e) => {
                  setNouveauMvt({ ...nouveauMvt, montant: e.target.value });
                }}
                placeholder="Montant"
                inputMode="decimal"
                className="bg-background w-28 rounded border px-2 py-1 text-right text-sm"
              />
              <Button type="submit" size="sm">
                Ajouter
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setNouveauMvt(null);
                }}
              >
                Annuler
              </Button>
            </form>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                setNouveauMvt({ libelle: '', sens: 'debit', montant: '', jour: '1' });
              }}
            >
              <Plus className="h-4 w-4" /> Ajouter un mouvement en {MOIS_LABELS[moisBanque]}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ---------- Ventes par mois ---------- */}
      {/* ---------- Marges : le récap, une ligne par chantier ---------- */}
      <Card className={onglet === 'marges' ? undefined : 'hidden'}>
        <CardHeader>
          <CardTitle>Récap des marges</CardTitle>
          <p className="text-muted-foreground text-sm">
            Une ligne par chantier et par mois : facture HT, achats réels, jours, équipe, heures —
            d’où la marge, la marge par jour et par heure. Les achats de chaque chantier se saisissent
            ici, fournisseur par fournisseur ; un récap Excel se reprend en un clic.
          </p>
        </CardHeader>
        <CardContent>
          {onglet === 'marges' ? <RecapMargesPanel annee={annee} /> : null}
        </CardContent>
      </Card>

      <Card className={onglet === 'ventes' ? undefined : 'hidden'}>
        <CardHeader>
          <CardTitle>Ventes par mois</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-left text-[11px] uppercase">
                <th className="py-1.5 pr-2 font-medium">Mois</th>
                <th className="px-2 py-1.5 text-right font-medium">Pièces</th>
                <th className="px-2 py-1.5 text-right font-medium">Total HT</th>
                <th className="px-2 py-1.5 text-right font-medium">TVA</th>
                <th className="px-2 py-1.5 text-right font-medium">Total TTC</th>
              </tr>
            </thead>
            <tbody>
              {ventesParMois.map((m, i) => {
                const ouvert = moisOuvert === i;
                const externes = externesParMois[i] ?? [];
                const crm = crmParMois[i] ?? [];
                return [
                  <tr
                    key={`mois-${String(i)}`}
                    className="border-muted/60 hover:bg-muted/40 cursor-pointer border-b"
                    onClick={() => {
                      setMoisOuvert(ouvert ? null : i);
                    }}
                  >
                    <td className="py-1 pr-2">
                      <span className="flex items-center gap-1 font-medium">
                        {ouvert ? (
                          <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="text-muted-foreground h-3.5 w-3.5" />
                        )}
                        {MOIS_LABELS[i]}
                      </span>
                    </td>
                    <td className="px-2 text-right tabular-nums">{m.nb || ''}</td>
                    <td className="px-2 text-right tabular-nums">{m.ht ? formatEuro(m.ht) : ''}</td>
                    <td className="px-2 text-right tabular-nums">
                      {m.tva ? formatEuro(m.tva) : ''}
                    </td>
                    <td className="px-2 text-right tabular-nums">
                      {m.ttc ? formatEuro(m.ttc) : ''}
                    </td>
                  </tr>,
                  ouvert ? (
                    <tr key={`detail-${String(i)}`}>
                      <td colSpan={5} className="bg-muted/20 px-3 pb-3 pt-1">
                        <table className="w-full border-collapse text-[12px]">
                          <thead>
                            <tr className="text-muted-foreground border-b text-left text-[10px] uppercase">
                              <th className="py-1 pr-2 font-medium">N° pièce</th>
                              <th className="px-1 py-1 font-medium">Date</th>
                              <th className="px-1 py-1 font-medium">Type</th>
                              <th className="px-1 py-1 text-right font-medium">HT 5,5 %</th>
                              <th className="px-1 py-1 text-right font-medium">HT 10 %</th>
                              <th className="px-1 py-1 text-right font-medium">HT 20 %</th>
                              <th className="px-1 py-1 text-right font-medium">HT exo</th>
                              <th className="px-1 py-1 text-right font-medium">Total HT</th>
                              <th className="w-7" />
                            </tr>
                          </thead>
                          <tbody>
                            {externes.map((v) => {
                              const totalHt =
                                Number(v.base_55) +
                                Number(v.base_10) +
                                Number(v.base_20) +
                                Number(v.base_exo);
                              return (
                                <tr key={v.id} className="border-muted/50 border-b">
                                  <td className="py-0.5 pr-2">
                                    <input
                                      key={`${v.id}-num-${v.numero}`}
                                      defaultValue={v.numero}
                                      className="hover:border-input focus:border-ring w-[110px] rounded border border-transparent bg-transparent px-1 py-0.5 font-medium focus:outline-none"
                                      onBlur={(e) => {
                                        const num = e.target.value.trim();
                                        if (num && num !== v.numero)
                                          venteUpdateMut.mutate({
                                            id: v.id,
                                            patch: { numero: num },
                                          });
                                      }}
                                    />
                                  </td>
                                  <td className="px-1">
                                    <input
                                      key={`${v.id}-date-${v.date_emission}`}
                                      type="date"
                                      defaultValue={v.date_emission}
                                      className="hover:border-input focus:border-ring rounded border border-transparent bg-transparent px-1 py-0.5 focus:outline-none"
                                      onBlur={(e) => {
                                        const d = e.target.value;
                                        if (d && d !== v.date_emission)
                                          venteUpdateMut.mutate({
                                            id: v.id,
                                            patch: { date_emission: d },
                                          });
                                      }}
                                    />
                                  </td>
                                  <td className="px-1">
                                    <select
                                      value={v.type}
                                      className="hover:border-input focus:border-ring rounded border border-transparent bg-transparent px-1 py-0.5 focus:outline-none"
                                      onChange={(e) => {
                                        venteUpdateMut.mutate({
                                          id: v.id,
                                          patch: { type: e.target.value as 'facture' | 'avoir' },
                                        });
                                      }}
                                    >
                                      <option value="facture">Facture</option>
                                      <option value="avoir">Avoir</option>
                                    </select>
                                  </td>
                                  {(['base_55', 'base_10', 'base_20', 'base_exo'] as const).map(
                                    (champ) => (
                                      <td key={champ} className="px-1 text-right">
                                        <VenteCell
                                          id={v.id}
                                          champ={champ}
                                          valeur={Number(v[champ])}
                                          onSave={majBaseVente}
                                        />
                                      </td>
                                    ),
                                  )}
                                  <td
                                    className={`px-1 text-right font-medium tabular-nums ${v.type === 'avoir' ? 'text-red-600' : ''}`}
                                  >
                                    {v.type === 'avoir' ? '−' : ''}
                                    {formatEuro(totalHt)}
                                  </td>
                                  <td className="text-center">
                                    <button
                                      type="button"
                                      title="Supprimer cette pièce"
                                      className="text-muted-foreground hover:text-destructive"
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Supprimer la pièce « ${v.numero} » (${v.date_emission}) ?`,
                                          )
                                        )
                                          venteDeleteMut.mutate(v.id);
                                      }}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                            {crm.map((f) => (
                              <tr
                                key={`crm-${f.numero ?? ''}-${f.date_emission ?? ''}`}
                                className="border-muted/50 text-muted-foreground border-b"
                              >
                                <td className="py-1 pr-2 font-medium">
                                  {f.numero ?? '(brouillon)'}
                                </td>
                                <td className="px-1">{f.date_emission}</td>
                                <td className="px-1">{f.kind === 'avoir' ? 'Avoir' : 'Facture'}</td>
                                <td colSpan={4} className="px-1 text-right italic">
                                  émise dans le CRM — non modifiable (NF525)
                                </td>
                                <td className="px-1 text-right font-medium tabular-nums">
                                  {formatEuro(Number(f.montant_ht))}
                                </td>
                                <td />
                              </tr>
                            ))}
                            {externes.length === 0 && crm.length === 0 ? (
                              <tr>
                                <td colSpan={9} className="text-muted-foreground py-2">
                                  Aucune pièce ce mois-ci.
                                </td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                        {ajoutVente?.mois === i ? (
                          <form
                            className="mt-2 flex flex-wrap items-center gap-2"
                            onSubmit={(e) => {
                              e.preventDefault();
                              const numero = ajoutVente.numero.trim().toUpperCase();
                              if (numero)
                                venteCreateMut.mutate({
                                  numero,
                                  type: ajoutVente.type,
                                  date_emission: `${String(annee)}-${String(i + 1).padStart(2, '0')}-15`,
                                });
                            }}
                          >
                            <input
                              value={ajoutVente.numero}
                              onChange={(e) => {
                                setAjoutVente({ ...ajoutVente, numero: e.target.value });
                              }}
                              placeholder="N° de pièce (ex. FA0000601)…"
                              className="bg-background w-56 rounded border px-2 py-1 text-sm"
                            />
                            <select
                              value={ajoutVente.type}
                              className="bg-background rounded border px-2 py-1 text-sm"
                              onChange={(e) => {
                                setAjoutVente({
                                  ...ajoutVente,
                                  type: e.target.value as 'facture' | 'avoir',
                                });
                              }}
                            >
                              <option value="facture">Facture</option>
                              <option value="avoir">Avoir</option>
                            </select>
                            <Button type="submit" size="sm">
                              Ajouter
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setAjoutVente(null);
                              }}
                            >
                              Annuler
                            </Button>
                          </form>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => {
                              setAjoutVente({ mois: i, numero: '', type: 'facture' });
                            }}
                          >
                            <Plus className="h-4 w-4" /> Ajouter une pièce en {MOIS_LABELS[i]}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ) : null,
                ];
              })}
              <tr className="bg-muted font-bold">
                <td className="py-1.5 pr-2">Total {annee}</td>
                <td className="px-2 text-right tabular-nums">{ventesTotal.nb}</td>
                <td className="px-2 text-right tabular-nums">{formatEuro(ventesTotal.ht)}</td>
                <td className="px-2 text-right tabular-nums">{formatEuro(ventesTotal.tva)}</td>
                <td className="px-2 text-right tabular-nums">{formatEuro(ventesTotal.ttc)}</td>
              </tr>
            </tbody>
          </table>
          <p className="text-muted-foreground mt-2 text-xs">
            Cumul des factures émises dans le CRM et de l’historique importé (Extrabat), avoirs
            déduits. Cliquez sur un mois pour voir, modifier ou ajouter des pièces — les factures
            émises dans le CRM restent en lecture seule (NF525).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
